import { Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, catchError, firstValueFrom, forkJoin, map, of, switchMap } from 'rxjs';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { faEye, faPencil } from '@fortawesome/free-solid-svg-icons';
import { TableColumn, TableAction } from '../../shared/models/table-models';
import { Table } from '../../shared/table/table';
import { Popup } from '../../../core/shared/components/popup/popup';
import { DynamicFieldConfig, FieldType } from '../../../core/shared/dynamic-form/form-config.model';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { MODULE_SEQ, PKL_GRP } from '../../../core/shared/models/picklist.model';
import { ToastService } from '../../../core/shared/services/toast.service';
import { Document, FoLtrDocFlags } from '../../leads/document/document';
import { DynamicFormComponent } from '../../../core/shared/dynamic-form/dynamic-form.component';
import { BackButton } from '../../shared/buttons/back-button';
import { CraditService } from '../shared/services/cradit.service';
import { FacilityOfferLetterExportService } from '../shared/services/facility-offer-letter-export.service';
import { Setup } from '../../setup/shared/service/setup';
import {
  hasRichTextContent,
  richTextForSave,
  richTextToPlainText,
} from '../shared/utils/rich-text.util';
import { RichTextEditor } from '../shared/components/rich-text-editor/rich-text-editor';
import {
  CreditProposalForLetterDto,
  CreditProposalForLetterRow,
  OfferLetterCovenant,
} from '../shared/models/credit-proposal-letter.model';
import {
  FO_LTR_DOC_SEQ,
  FO_LTR_LOH_DOC_SEQ,
  FO_LTR_ROLES,
  FO_LTR_STATUS,
  FO_LTR_STATUS_LABEL_KEYS,
  buildFoLtrSignedCopyFormData,
  currentFoLtrRole,
  hasFoLtrRole,
} from '../shared/models/fo-ltr-status.model';

/** Covenants questionnaire — same `qstnrSeq` the Summary Terms covenant picker uses. */
const COVENANTS_QSTNR_SEQ = 5;

/** Credit proposal block that stores the Summary Terms and Conditions rows. */
const SUMMARY_TERMS_REF_VAL_SEQ = 2682;

@Component({
  selector: 'app-facilites-list',
  imports: [
    CommonModule,
    FormsModule,
    Table,
    Popup,
    Document,
    BackButton,
    TranslatePipe,
    DynamicFormComponent,
    RichTextEditor,
  ],
  templateUrl: './facilites-list.html',
  styleUrl: './facilites-list.css',
})
export class FacilitesList implements OnInit {
  constructor(
    private creditService: CraditService,
    private pickListService: PickListService,
    private toast: ToastService,
    private offerLetterExport: FacilityOfferLetterExportService,
    private setup: Setup,
    private router: Router,
    private translate: TranslateService,
  ) {}

  downloadingOfferLetterSeq: number | null = null;

  // ================= TABS =================
  activeTab:
    | 'all'
    | 'clientRecommendation'
    | 'pendingDrafts'
    | 'clientApproval'
    | 'roPendingReview'
    | 'sacReview'
    | 'pendingApproval'
    | 'approved'
    | 'extension'
    | 'pendingExtension'
    | 'legalOpinion' = 'all';

  setTab(
    tab:
      | 'all'
      | 'clientRecommendation'
      | 'pendingDrafts'
      | 'clientApproval'
      | 'roPendingReview'
      | 'sacReview'
      | 'pendingApproval'
      | 'approved'
      | 'extension'
      | 'pendingExtension'
      | 'legalOpinion',
  ): void {
    this.activeTab = tab;

    if (tab === 'pendingExtension') {
      this.backToPendingExtensionClients();
      this.loadPendingExtensionList();
    }

    if (tab === 'extension') {
      this.backToExtensionClients();
      this.loadExtensionList();
    }

    if (tab === 'approved' && !this.approvedLettersLoaded) {
      this.loadApprovedCreditProposalsForLetter();
    }

    if (tab === 'pendingDrafts') {
      this.backToPendingDraftClients();
      this.loadPendingDraftDocuments();
    }

    if (tab === 'clientApproval') {
      this.backToClientApprovalClients();
      this.loadClientApprovalList();
    }

    if (tab === 'roPendingReview') {
      this.backToRoReviewClients();
      this.loadRoReviewClientList();
    }

    if (tab === 'sacReview') {
      this.backToSacReviewClients();
      this.loadSacReviewClientList();
    }

    if (tab === 'pendingApproval') {
      this.backToPendingApprovalClients();
      this.loadPendingApprovalDocuments();
    }

    if (tab === 'clientRecommendation') {
      this.loadClientRecommendations();
    }

    if (tab === 'legalOpinion') {
      this.backToLegalOpinionList();
      this.loadLegalOpinionList();
      this.loadLegalOpinionDocType();
    }
  }

  // ================= OFFER LETTER LIST =================
  creditProposalList: CreditProposalForLetterRow[] = [];
  creditProposalColumns: TableColumn[] = [];
  creditProposalLoading = false;
  creditProposalError = '';

  // ================= APPROVED OFFER LETTER LIST =================
  approvedLetterList: CreditProposalForLetterRow[] = [];
  approvedLettersLoading = false;
  approvedLettersError = '';
  private approvedLettersLoaded = false;

  // ================= PENDING DRAFTS =================
  /**
   * Draft offer letter documents waiting on the Relationship Officer
   * (LNLY_RO) — the only role this tab exists for.
   */
  pendingDraftList: any[] = [];
  pendingDraftColumns: TableColumn[] = [];
  pendingDraftsLoading = false;
  pendingDraftsError = '';

  // ================= CLIENT APPROVAL =================
  /**
   * Clients whose offer letter acceptance is pending the Relationship
   * Officer (LNLY_RO) — the only role this tab exists for.
   */
  clientApprovalList: any[] = [];
  clientApprovalColumns: TableColumn[] = [];
  clientApprovalLoading = false;
  clientApprovalError = '';

  /** The client whose documents are open (institution name clicked), or null for the client list. */
  clientApprovalClient: any = null;
  clientApprovalDocList: any[] = [];
  clientApprovalDocColumns: TableColumn[] = [];
  clientApprovalDocsLoading = false;
  clientApprovalDocsError = '';

  /** Client row whose documents are open; null shows the client list. */
  pendingDraftClient: any = null;
  pendingDraftReadOnly = false;
  pendingDraftDocList: any[] = [];
  pendingDraftDocColumns: TableColumn[] = [];
  pendingDraftDocsLoading = false;
  pendingDraftDocsError = '';

  get isRelationshipOfficer(): boolean {
    return hasFoLtrRole(FO_LTR_ROLES.RO);
  }

  /** Team Lead (LNLY_TL) — sees their ROs' Client Approval / Pending Review lists, read-only. */
  get isTeamLead(): boolean {
    return hasFoLtrRole(FO_LTR_ROLES.TL);
  }

  /** RO or TL — the roles the Client Approval and Pending Review tabs exist for. */
  get isRoOrTeamLead(): boolean {
    return this.isRelationshipOfficer || this.isTeamLead;
  }

  /** Shariah Advisor (SA / SAC) — reviews Mudarabah / Islamic-term issue documents (4812). */
  get isSac(): boolean {
    return hasFoLtrRole(FO_LTR_ROLES.SAC);
  }

  // ================= PENDING ACTIONS (LNLY_AM) =================
  /**
   * FOL new flow, Stage 2: issue documents waiting on the Account Manager
   * (am-doc-pndng-lst) — 4813 (RO approved, signed copy due) and 4814 (RO
   * recommendation to act on). The client list is the approved letters
   * (aprvd-lst), since issue documents only exist on letters at 902.
   */
  pendingApprovalList: any[] = [];
  pendingApprovalColumns: TableColumn[] = [];
  pendingApprovalLoading = false;
  pendingApprovalError = '';

  /** Client row whose documents are open; null shows the client list. */
  pendingApprovalClient: any = null;
  pendingApprovalReadOnly = false;
  pendingApprovalDocList: any[] = [];
  pendingApprovalDocColumns: TableColumn[] = [];
  pendingApprovalDocsLoading = false;
  pendingApprovalDocsError = '';

  pendingApprovalActions: TableAction[] = [
    {
      icon: 'images/upload.png',
      tooltip: 'FACILITIES.UPLOAD_SIGNED_COPY',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openRowUploadPopup(row, 'signedCopy'),
      visible: (row: any) => Number(row?.stsKey) === FO_LTR_STATUS.RO_APPROVED_SIGNED_COPY_PENDING,
      keepSpace: true,
    },
    {
      icon: 'images/upload.png',
      tooltip: 'FACILITIES.REUPLOAD_DOCUMENT',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openRowUploadPopup(row, 'reupload'),
      visible: (row: any) => Number(row?.stsKey) === FO_LTR_STATUS.RO_RECOMMENDATION_PENDING_AM,
    },
    {
      // viewIcon is declared further down — faEye is the same icon.
      icon: faEye,
      tooltip: 'FACILITIES.VIEW_RECOMMENDATION',
      class: 'action-view',
      onClick: (row: any) => this.viewRecommendation(row),
      visible: (row: any) => Number(row?.stsKey) === FO_LTR_STATUS.RO_RECOMMENDATION_PENDING_AM,
    },
  ];

  // ================= PENDING REVIEW (LNLY_RO / LNLY_TL) =================
  /**
   * Issue documents at 2658 (first upload, or SAC-approved) and signed
   * copies at 4815, waiting on the Relationship Officer. The Team Lead sees
   * their ROs' lists read-only; only the RO (own FOL) approves / recommends.
   */
  roReviewList: any[] = [];
  roReviewLoading = false;
  roReviewError = '';

  roReviewClient: any = null;
  roReviewDocList: any[] = [];
  roReviewDocColumns: TableColumn[] = [];
  roReviewDocsLoading = false;
  roReviewDocsError = '';

  roReviewColumns: TableColumn[] = [
    { key: 'crdtFoLtrSeq', label: 'FACILITIES.OFFER_LETTER_ID', type: 'text' },
    {
      key: 'clntNm',
      label: 'FACILITIES.INSTITUTION_NAME',
      type: 'link',
      onClick: (row: any) => this.openRoReviewDocuments(row),
    },
    { key: 'docCnt', label: 'FACILITIES.TOTAL_DOCUMENTS', type: 'text' },
  ];

  roReviewActions: TableAction[] = [
    {
      icon: 'images/Tick-2.png',
      tooltip: 'COMMON.APPROVE',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-green-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openDocDecisionPopup(row, 'roApprove'),
    },
    {
      icon: 'images/edit-icon.png',
      tooltip: 'FACILITIES.RECOMMENDATIONS',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openDocDecisionPopup(row, 'roRecommend'),
    },
  ];

  /** GET /crdt-prpsl/fo-ltr/ro-pndng-clnt-lst */
  loadRoReviewClientList(): void {
    this.roReviewLoading = true;
    this.roReviewError = '';

    this.creditService.getRoPendingFoLtrClientList().subscribe({
      next: (response: any) => {
        this.roReviewList = this.mapClientRows(this.unwrapList(response));
        this.roReviewLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load pending review clients:', error);

        this.roReviewList = [];
        this.roReviewError = 'FACILITIES.ERR.LOAD_PENDING_REVIEW';
        this.roReviewLoading = false;
      },
    });
  }

  openRoReviewDocuments(row: any): void {
    this.roReviewClient = row;
    this.loadRoReviewDocuments();
  }

  backToRoReviewClients(): void {
    this.roReviewClient = null;
    this.roReviewDocList = [];
    this.roReviewDocsError = '';
  }

  /** GET /crdt-prpsl/fo-ltr/ro-pndng-lst?crdtFoLtrSeq= */
  loadRoReviewDocuments(): void {
    const crdtFoLtrSeq = Number(this.roReviewClient?.crdtFoLtrSeq ?? 0);

    if (crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_MISSING');
      return;
    }

    this.roReviewDocsLoading = true;
    this.roReviewDocsError = '';

    this.creditService.getRoPendingFoLtrDocuments(crdtFoLtrSeq).subscribe({
      next: (response: any) => {
        this.roReviewDocList = this.unwrapList(response)
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => this.mapPendingDraftToRow(item));

        this.roReviewDocsLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load pending review documents:', error);

        this.roReviewDocList = [];
        this.roReviewDocsError = 'FACILITIES.ERR.LOAD_PENDING_REVIEW_DOCS';
        this.roReviewDocsLoading = false;
      },
    });
  }

  private refreshRoReview(): void {
    this.loadRoReviewClientList();

    if (this.roReviewClient) {
      this.loadRoReviewDocuments();
    }
  }

  // ================= SAC REVIEW (SAC) =================
  /** Islamic-term issue documents at 4812 (first upload), waiting on the SAC. */
  sacReviewList: any[] = [];
  sacReviewLoading = false;
  sacReviewError = '';

  sacReviewClient: any = null;
  sacReviewDocList: any[] = [];
  sacReviewDocColumns: TableColumn[] = [];
  sacReviewDocsLoading = false;
  sacReviewDocsError = '';

  sacReviewColumns: TableColumn[] = [
    { key: 'crdtFoLtrSeq', label: 'FACILITIES.OFFER_LETTER_ID', type: 'text' },
    {
      key: 'clntNm',
      label: 'FACILITIES.INSTITUTION_NAME',
      type: 'link',
      onClick: (row: any) => this.openSacReviewDocuments(row),
    },
    { key: 'docCnt', label: 'FACILITIES.TOTAL_DOCUMENTS', type: 'text' },
  ];

  sacReviewActions: TableAction[] = [
    {
      icon: 'images/Tick-2.png',
      tooltip: 'COMMON.APPROVE',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-green-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openDocDecisionPopup(row, 'sacApprove'),
    },
    {
      icon: 'images/cancel.png',
      tooltip: 'COMMON.REJECT',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-red-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openDocDecisionPopup(row, 'sacReject'),
    },
  ];

  /** GET /crdt-prpsl/fo-ltr/sac-pndng-clnt-lst */
  loadSacReviewClientList(): void {
    this.sacReviewLoading = true;
    this.sacReviewError = '';

    this.creditService.getSacPendingFoLtrClientList().subscribe({
      next: (response: any) => {
        this.sacReviewList = this.mapClientRows(this.unwrapList(response));
        this.sacReviewLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load SAC review clients:', error);

        this.sacReviewList = [];
        this.sacReviewError = 'FACILITIES.ERR.LOAD_SAC_REVIEW';
        this.sacReviewLoading = false;
      },
    });
  }

  openSacReviewDocuments(row: any): void {
    this.sacReviewClient = row;
    this.loadSacReviewDocuments();
  }

  backToSacReviewClients(): void {
    this.sacReviewClient = null;
    this.sacReviewDocList = [];
    this.sacReviewDocsError = '';
  }

  /** GET /crdt-prpsl/fo-ltr/sac-pndng-lst?crdtFoLtrSeq= */
  loadSacReviewDocuments(): void {
    const crdtFoLtrSeq = Number(this.sacReviewClient?.crdtFoLtrSeq ?? 0);

    if (crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_MISSING');
      return;
    }

    this.sacReviewDocsLoading = true;
    this.sacReviewDocsError = '';

    this.creditService.getSacPendingFoLtrDocuments(crdtFoLtrSeq).subscribe({
      next: (response: any) => {
        this.sacReviewDocList = this.unwrapList(response)
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => this.mapPendingDraftToRow(item));

        this.sacReviewDocsLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load SAC review documents:', error);

        this.sacReviewDocList = [];
        this.sacReviewDocsError = 'FACILITIES.ERR.LOAD_SAC_REVIEW_DOCS';
        this.sacReviewDocsLoading = false;
      },
    });
  }

  private refreshSacReview(): void {
    this.loadSacReviewClientList();

    if (this.sacReviewClient) {
      this.loadSacReviewDocuments();
    }
  }

  // ================= DOCUMENT DECISION (RO approve / recommend, SAC approve / reject) =================
  /** One comment popup for the four Stage 2 decisions; the comment is required except on approvals. */
  showDocDecisionPopup = false;
  docDecisionMode: 'roApprove' | 'roRecommend' | 'sacApprove' | 'sacReject' = 'roApprove';
  docDecisionFields: DynamicFieldConfig[] = [];
  docDecisionFormData: any = {};
  private docDecisionDocSeq: number | null = null;

  get docDecisionTitle(): string {
    switch (this.docDecisionMode) {
      case 'roRecommend':
        return 'FACILITIES.RECOMMENDATIONS';
      case 'sacReject':
        return 'FACILITIES.REJECT_DOCUMENT';
      default:
        return 'FACILITIES.APPROVE_DOCUMENT';
    }
  }

  openDocDecisionPopup(
    row: any,
    mode: 'roApprove' | 'roRecommend' | 'sacApprove' | 'sacReject',
  ): void {
    const crdtFoLtrDocSeq = Number(row?.crdtFoLtrDocSeq ?? 0);

    if (crdtFoLtrDocSeq <= 0) {
      this.toast.error('FACILITIES.MSG.DOC_SEQ_MISSING');
      return;
    }

    const commentRequired = mode === 'roRecommend' || mode === 'sacReject';

    this.docDecisionDocSeq = crdtFoLtrDocSeq;
    this.docDecisionMode = mode;
    this.docDecisionFormData = { cmmnt: '' };
    this.docDecisionFields = [
      {
        key: 'cmmnt',
        label: 'FACILITIES.COMMENT',
        type: 'textarea',
        validations: commentRequired ? { required: true } : {},
      },
    ];

    this.showDocDecisionPopup = true;
  }

  /** POST ro-doc-aprv | ro-doc-rcmnd | sac-aprv | sac-rjct — { crdtFoLtrDocSeq, cmmnt } */
  handleDocDecisionSave = (form: any) => {
    const crdtFoLtrDocSeq = Number(this.docDecisionDocSeq ?? 0);

    if (crdtFoLtrDocSeq <= 0) {
      this.toast.error('FACILITIES.MSG.DOC_SEQ_MISSING');
      return false;
    }

    const mode = this.docDecisionMode;
    const payload = { crdtFoLtrDocSeq, cmmnt: (form?.cmmnt || '').trim() };

    const request$ =
      mode === 'roApprove'
        ? this.creditService.roApproveFoLtrDocument(payload)
        : mode === 'roRecommend'
          ? this.creditService.roRecommendFoLtrDocument(payload)
          : mode === 'sacApprove'
            ? this.creditService.sacApproveFoLtrDocument(payload)
            : this.creditService.sacRejectFoLtrDocument(payload);

    const successKey =
      mode === 'roRecommend'
        ? 'FACILITIES.MSG.RECOMMENDATION_ADDED'
        : mode === 'sacReject'
          ? 'FACILITIES.MSG.DOC_REJECTED'
          : 'FACILITIES.MSG.DOC_APPROVED_SUCCESS';

    const failureKey =
      mode === 'roRecommend'
        ? 'FACILITIES.MSG.UNABLE_ADD_RECOMMENDATION'
        : mode === 'sacReject'
          ? 'FACILITIES.MSG.UNABLE_REJECT_DOC'
          : 'FACILITIES.MSG.UNABLE_APPROVE_DOC';

    return request$.subscribe({
      next: (response: any) => {
        if (response?.__hasError) {
          return;
        }

        if (response?.error) {
          this.toast.error(response.error);
          return;
        }

        this.toast.success(response?.message || successKey);

        this.docDecisionDocSeq = null;

        if (mode === 'roApprove' || mode === 'roRecommend') {
          this.refreshRoReview();
        } else {
          this.refreshSacReview();
        }
      },

      error: (error: any) => {
        console.error('Document decision failed:', error);

        this.toast.error(error?.error?.message || error?.error?.error || failureKey);
      },
    });
  };

  /** List endpoints return an array, `{ data: [...] }` or a single object. */
  private unwrapList(response: any): any[] {
    return Array.isArray(response)
      ? response
      : Array.isArray(response?.data)
        ? response.data
        : response
          ? [response]
          : [];
  }

  /** Client rows (one per offer letter) for the *-clnt-lst endpoints. */
  private mapClientRows(rows: any[]): any[] {
    return rows
      .filter((item) => Number(item?.delFlg ?? 0) !== 1)
      .map((item) => ({
        ...item,
        crdtFoLtrSeq: item?.crdtFoLtrSeq != null ? Number(item.crdtFoLtrSeq) : null,
        clntNm: item?.clntNm || '-',
        docCnt: item?.docCnt != null ? Number(item.docCnt) : 0,
      }));
  }

  // ================= CLIENT RECOMMENDATIONS =================
  /**
   * Documents whose recommendations the VCM (LNLY_VCM) must act on — the
   * only role this tab exists for.
   */
  clientRecommendationList: any[] = [];
  clientRecommendationColumns: TableColumn[] = [];
  clientRecommendationsLoading = false;
  clientRecommendationsError = '';

  get isVcm(): boolean {
    return hasFoLtrRole(FO_LTR_ROLES.VCM);
  }

  /** The same eye icon the onboarding tables use for View. */
  readonly viewIcon = faEye;

  /** The same pencil icon the setup tables use for Edit. */
  readonly editIcon = faPencil;

  clientRecommendationActions: TableAction[] = [
    {
      icon: this.viewIcon,
      tooltip: 'FACILITIES.VIEW_RECOMMENDATION',
      class: 'action-view',
      onClick: (row: any) => this.viewRecommendation(row),
    },
    {
      icon: this.editIcon,
      tooltip: 'FACILITIES.EDIT_TERM_PARAMETERS',
      class: 'action-view',
      onClick: (row: any) => this.openTermParamsPopup(row),
    },
    {
      // Same reject icon, at the same default size, as the Tasks screen.
      icon: 'images/cancel.png',
      tooltip: 'COMMON.REJECT',
      onClick: (row: any) => this.openRejectRecommendationPopup(row),
    },
  ];

  // ================= REJECT RECOMMENDATION =================
  showRejectRecommendationPopup = false;
  rejectRecommendationFields: DynamicFieldConfig[] = [];
  rejectRecommendationFormData: any = {};
  private rejectRecommendationDocSeq: number | null = null;

  openRejectRecommendationPopup(row: any): void {
    const crdtFoLtrDocSeq = Number(row?.crdtFoLtrDocSeq ?? 0);

    if (crdtFoLtrDocSeq <= 0) {
      this.toast.error('FACILITIES.MSG.DOC_SEQ_MISSING');
      return;
    }

    this.rejectRecommendationDocSeq = crdtFoLtrDocSeq;

    this.rejectRecommendationFormData = { cmmnt: '' };

    this.rejectRecommendationFields = [
      {
        key: 'cmmnt',
        label: 'FACILITIES.COMMENT',
        type: 'text',
        validations: {
          required: true,
        },
      },
    ];

    this.showRejectRecommendationPopup = true;
  }

  /** POST /crdt-prpsl/fo-ltr/vcm-reject-recommendation */
  handleRejectRecommendationSave = (form: any) => {
    const crdtFoLtrDocSeq = Number(this.rejectRecommendationDocSeq ?? 0);

    if (crdtFoLtrDocSeq <= 0) {
      this.toast.error('FACILITIES.MSG.DOC_SEQ_MISSING');
      return;
    }

    this.creditService
      .vcmRejectFoLtrRecommendation({
        crdtFoLtrDocSeq,
        cmmnt: form?.cmmnt || '',
      })
      .subscribe({
        next: (response: any) => {
          if (response?.error) {
            this.toast.error(response.error);
            return;
          }

          this.toast.success(response?.message || 'FACILITIES.MSG.RECOMMENDATION_REJECTED');

          this.rejectRecommendationDocSeq = null;

          this.loadClientRecommendations();
        },

        error: (error: any) => {
          console.error('Unable to reject recommendation:', error);

          this.toast.error(
            error?.error?.message || error?.error?.error || 'FACILITIES.MSG.UNABLE_REJECT_RECOMMENDATION',
          );
        },
      });
  };

  // ================= TERM PARAMETERS =================
  showTermParamsPopup = false;
  termParamsLoading = false;
  termParamsSaving = false;

  /**
   * One tab per repayment term type (`typ`, ref code group 13 refCd — 17
   * Conventional, 18 Islamic, ...) that `trm-params` returned. `raw` is
   * exactly what the GET sent, so the keys the popup doesn't show go back unchanged.
   */
  termParamTabs: { typ: number | null; label: string; raw: any; fields: DynamicFieldConfig[]; model: any }[] = [];
  activeTermParamTab = 0;

  /**
   * Term types never shown as a tab (30 = NPL). Their rows are still sent
   * back unchanged on save, so the letter's snapshot keeps them.
   */
  private readonly hiddenTermParamTypes = [30];
  private hiddenTermParamRows: any[] = [];

  /** Optional comment for vcm-approve-recommendation — one for all types. */
  termParamsComment = '';

  /**
   * Only the active tab's form is rendered (field ids repeat across tabs, so
   * hidden forms would steal each other's toggle clicks); its values are
   * kept in the tab's `model` when switching away.
   */
  @ViewChildren('termParamsForm')
  private set termParamsFormList(list: QueryList<DynamicFormComponent> | undefined) {
    this.termParamsForms = list;
    list?.forEach((form) => this.wireRenewableToggle(form));
  }

  private termParamsForms?: QueryList<DynamicFormComponent>;

  /** Forms whose Renewable toggle is already wired, so each is subscribed once. */
  private readonly wiredRenewableForms = new WeakSet<DynamicFormComponent>();

  /** Renewable Flag Yes enables Renewable Amount; No disables it and sends 0. */
  private wireRenewableToggle(component: DynamicFormComponent): void {
    const flag = component.form?.get('rnwAblFlg');
    const amount = component.form?.get('rnwAblAmnt');

    if (!flag || !amount || this.wiredRenewableForms.has(component)) {
      return;
    }

    this.wiredRenewableForms.add(component);

    const apply = (value: unknown): void => {
      if (Number(value) === 1) {
        amount.enable({ emitEvent: false });
      } else {
        amount.setValue(0, { emitEvent: false });
        amount.disable({ emitEvent: false });
      }
    };

    // Runs inside change detection (query update), so the first state is set on the next tick.
    setTimeout(() => apply(flag.value));
    flag.valueChanges.subscribe(apply);
  }

  /** The document row the popup was opened from — its seq goes with the save. */
  private termParamsDocSeq: number | null = null;

  /**
   * The term values shown in the popup, in this order, with the labels the
   * business uses. Anything else the endpoint returns (audit columns, flags,
   * nested lists) stays out.
   */
  private readonly termParamDefinitions: { key: string; label: string; type: FieldType }[] = [
    { key: 'prpsdExpsr', label: 'FACILITIES.TERM.PROPOSED_EXPOSURE', type: 'number' },
    { key: 'dsbmntAmount', label: 'FACILITIES.TERM.DISBURSEMENT_AMOUNT', type: 'number' },
    { key: 'dsbStrtDt', label: 'FACILITIES.TERM.DISBURSEMENT_START_DATE', type: 'date' },
    { key: 'dsbEndDt', label: 'FACILITIES.TERM.DISBURSEMENT_END_DATE', type: 'date' },
    { key: 'rpStrtDt', label: 'FACILITIES.TERM.REPAYMENT_START_DATE', type: 'date' },
    { key: 'rpEndDt', label: 'FACILITIES.TERM.REPAYMENT_END_DATE', type: 'date' },
    { key: 'rpymntFrqncy', label: 'FACILITIES.TERM.REPAYMENT_FREQUENCY', type: 'select' },
    { key: 'noOfInstlmnt', label: 'FACILITIES.TERM.NO_OF_INSTALLMENT', type: 'number' },
    { key: 'grcPrd', label: 'FACILITIES.TERM.GRACE_PERIOD', type: 'number' },
    { key: 'kbrRt', label: 'FACILITIES.TERM.KIBOR_RATE', type: 'number' },
    { key: 'kbrRstPrd', label: 'FACILITIES.TERM.KIBOR_RESET_PERIOD', type: 'select' },
    { key: 'sprdRt', label: 'FACILITIES.TERM.SPREAD_RATE', type: 'number' },
    { key: 'rnwAblFlg', label: 'FACILITIES.TERM.RENEWABLE_FLAG', type: 'radio' },
    { key: 'rnwAblAmnt', label: 'FACILITIES.TERM.RENEWABLE_AMOUNT', type: 'number' },
  ];

  /** GET /crdt-prpsl/fo-ltr/trm-params/{crdtFoLtrSeq} */
  openTermParamsPopup(row: any): void {
    const crdtFoLtrSeq = Number(row?.crdtFoLtrSeq ?? 0);

    if (crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_MISSING');
      return;
    }

    this.termParamTabs = [];
    this.hiddenTermParamRows = [];
    this.activeTermParamTab = 0;
    this.termParamsComment = '';
    this.termParamsDocSeq = Number(row?.crdtFoLtrDocSeq ?? 0) || null;
    this.termParamsLoading = true;
    this.showTermParamsPopup = true;

    // Tab names come from ref code group 13 (repayment term types).
    forkJoin({
      response: this.creditService.getFoLtrTermParams(crdtFoLtrSeq),
      types: this.pickListService.loadCodesAsync([PKL_GRP.FACILITY_TYPE]).pipe(catchError(() => of(null))),
    }).subscribe({
      next: ({ response }) => {
        const list = this.unwrapList(response).filter(
          (params) => params && typeof params === 'object' && Number(params.delFlg ?? 0) !== 1,
        );
        const typeOptions = this.pickListService.getOptionsSync(PKL_GRP.FACILITY_TYPE);

        this.hiddenTermParamRows = list.filter((params) =>
          this.hiddenTermParamTypes.includes(Number(params.typ)),
        );

        this.termParamTabs = list
          .filter((params) => !this.hiddenTermParamTypes.includes(Number(params.typ)))
          .map((params) => {
            const typ = params.typ != null ? Number(params.typ) : null;
            const option = typeOptions.find((candidate) => Number(candidate.original?.refCd) === typ);

            return {
              typ,
              label: String(option?.label ?? '').trim() || (typ != null ? String(typ) : '-'),
              raw: params,
              fields: this.buildTermParamsFields(params),
              model: this.buildTermParamsFormData(params),
            };
          });

        this.termParamsLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load term parameters:', error);

        this.termParamTabs = [];
        this.termParamsLoading = false;

        this.toast.error('FACILITIES.MSG.UNABLE_LOAD_TERM_PARAMS');
      },
    });
  }

  /** Editable fields for the popup, only for the values the endpoint returned. */
  private buildTermParamsFields(params: any): DynamicFieldConfig[] {
    if (!params || typeof params !== 'object') {
      return [];
    }

    return this.termParamDefinitions
      .filter((definition) => definition.key in params)
      .map((definition) => ({
        key: definition.key,
        label: definition.label,
        type: definition.type,
        // Kibor Rate is shown as trm-params returned it and never edited.
        disabled: definition.key === 'kbrRt',
        options$:
          definition.key === 'rpymntFrqncy'
            ? this.pickListService.getOptions(PKL_GRP.DISBURSEMENT_FREQUENCY)
            : definition.key === 'kbrRstPrd'
              ? this.pickListService.getOptions(PKL_GRP.KIBOR_RESET_PERIOD)
              : definition.key === 'rnwAblFlg'
                ? // Same Yes / No toggle as the credit proposal's Repayment Terms.
                  [
                    { label: 'COMMON.YES', value: 1 },
                    { label: 'COMMON.NO', value: 0 },
                  ]
                : undefined,
      }));
  }

  selectTermParamTab(index: number): void {
    this.keepActiveTermParamValues();
    this.activeTermParamTab = index;
  }

  /** Copies the open tab's form values into its model, so they survive a tab switch. */
  private keepActiveTermParamValues(): void {
    const tab = this.termParamTabs[this.activeTermParamTab];
    const values = this.termParamsForms?.first?.form?.getRawValue();

    if (tab && values) {
      tab.model = { ...tab.model, ...values };
    }
  }

  /** Edited values of every tab, in tab order. */
  private termParamsFormValues(): any[] {
    this.keepActiveTermParamValues();

    return this.termParamTabs.map((tab) => tab.model ?? {});
  }

  /** One `trm-params` row with the popup's edits, every key the popup doesn't show sent back as the GET returned it. */
  private buildTermParamsPayloadItem(raw: any, form: any): any {
    /** Edited value when the popup showed the field, otherwise what the GET returned. */
    const edited = (key: string) => (key in (form ?? {}) ? form[key] : (raw[key] ?? null));

    return {
      crdtTrmParamsSeq: raw.crdtTrmParamsSeq ?? null,
      crdtPrpslSeq: raw.crdtPrpslSeq ?? null,
      typ: raw.typ ?? null,
      dsbmntAmount: edited('dsbmntAmount'),
      rnwAblAmnt: edited('rnwAblAmnt'),
      rnwAblFlg: edited('rnwAblFlg'),
      prmFlg: raw.prmFlg ?? null,
      prmAmnt: raw.prmAmnt ?? null,
      dsbStrtDt: edited('dsbStrtDt'),
      dsbEndDt: edited('dsbEndDt'),
      vkbrFlg: raw.vkbrFlg ?? null,
      kbrRt: edited('kbrRt'),
      kbrRstPrd: edited('kbrRstPrd'),
      vkbrRt: raw.vkbrRt ?? null,
      sprdRt: edited('sprdRt'),
      grntrFlg: raw.grntrFlg ?? null,
      rpymntFrqncy: edited('rpymntFrqncy'),
      arrearFlg: raw.arrearFlg ?? null,
      grcPrd: edited('grcPrd'),
      noOfInstlmnt: edited('noOfInstlmnt'),
      rpStrtDt: edited('rpStrtDt'),
      rpEndDt: edited('rpEndDt'),
      upfrntFlg: raw.upfrntFlg ?? raw.uprfrntFlg ?? null,
      prpsdExpsr: edited('prpsdExpsr'),
      upFrntFee: raw.upFrntFee ?? null,
      crdtEnsrFee: raw.crdtEnsrFee ?? null,
      delFlg: raw.delFlg ?? 0,
      // The GET spells this one `vKbrStrt`; the payload takes `vkbrStrt`.
      vkbrStrt: raw.vkbrStrt ?? raw.vKbrStrt ?? null,
      // crtdBy / crtdDt / lastUpdBy / lastUpdDt are the backend's own audit
      // columns — never sent from here.
    };
  }

  /**
   * POST /crdt-prpsl/fo-ltr/vcm-approve-recommendation — every type (tab) in
   * `crdtTrmParamsLst`, with its edited values.
   */
  saveTermParams(): void {
    if (this.termParamsSaving || this.termParamTabs.length === 0) {
      return;
    }

    const formValues = this.termParamsFormValues();
    const cmmnt = this.termParamsComment.trim();

    // Live crdt_trm_params is never changed: when the values differ, the
    // backend keeps a snapshot for this letter (crdt_trm_params_fol).
    const payload = {
      crdtFoLtrDocSeq: this.termParamsDocSeq,
      crdtTrmParamsLst: [
        ...this.termParamTabs.map((tab, index) =>
          this.buildTermParamsPayloadItem(tab.raw ?? {}, formValues[index]),
        ),
        // Hidden types (NPL) go back exactly as trm-params returned them.
        ...this.hiddenTermParamRows.map((raw) => this.buildTermParamsPayloadItem(raw, {})),
      ],
      ...(cmmnt ? { cmmnt } : {}),
    };

    this.termParamsSaving = true;

    this.creditService.vcmApproveFoLtrRecommendation(payload).subscribe({
      next: (response: any) => {
        this.termParamsSaving = false;

        // A 200 can still carry an error body: the error interceptor has already toasted it.
        if (response?.__hasError) {
          return;
        }

        if (response?.error) {
          this.toast.error(response.error);
          return;
        }

        this.toast.success(response?.message || 'FACILITIES.MSG.TERM_PARAMS_SAVED');

        this.showTermParamsPopup = false;
        this.loadClientRecommendations();
      },

      error: (error: any) => {
        console.error('Unable to save term parameters:', error);

        this.termParamsSaving = false;
        this.toast.error(
          error?.error?.message || error?.error?.error || 'FACILITIES.MSG.UNABLE_SAVE_TERM_PARAMS',
        );
      },
    });
  }

  /** The same values, keyed for the form. */
  private buildTermParamsFormData(params: any): any {
    if (!params || typeof params !== 'object') {
      return {};
    }

    const data: any = {};

    for (const definition of this.termParamDefinitions) {
      if (definition.key in params) {
        data[definition.key] = params[definition.key];
      }
    }

    return data;
  }

  showRecommendationViewPopup = false;
  recommendationViewLoading = false;
  recommendationComments: string[] = [];

  /**
   * GET /crdt-prpsl/fo-ltr/fo-ltr-rvw/{entySeq}/13 — recommendation comments
   * are saved in wrkflw_cmnt against the document row (enty_seq =
   * crdtFoLtrDocSeq), so that is what is sent; the letter seq is only the
   * fallback for rows that don't carry a document seq.
   */
  viewRecommendation(row: any): void {
    const entySeq = Number(row?.crdtFoLtrDocSeq ?? row?.crdtFoLtrSeq ?? 0);

    if (entySeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_MISSING');
      return;
    }

    this.recommendationComments = [];
    this.recommendationViewLoading = true;
    this.showRecommendationViewPopup = true;

    this.creditService
      .getFoLtrReview(entySeq, this.letterDocumentModuleSeq)
      .subscribe({
        next: (response: any) => {
          const rows: any[] = Array.isArray(response)
            ? response
            : Array.isArray(response?.data)
              ? response.data
              : response
                ? [response]
                : [];

          this.recommendationComments = rows
            .map((item) => (item?.cmmnt ?? '').toString().trim())
            .filter((comment) => !!comment);

          this.recommendationViewLoading = false;
        },

        error: (error: any) => {
          console.error('Unable to load recommendation:', error);

          this.recommendationComments = [];
          this.recommendationViewLoading = false;

          this.toast.error('FACILITIES.MSG.UNABLE_LOAD_RECOMMENDATION');
        },
      });
  }

  // ================= OFFER LETTER DOCUMENTS POPUP =================
  /**
   * Documents for the offer letter live under their own document category
   * (`GET /setup/get-all-docs/13`) and are stored against the letter sequence.
   */
  readonly letterDocumentModuleSeq = MODULE_SEQ.FACILITY_OFFER_LETTER;

  /**
   * An Account Manager (LNLY_AM) uploads only the Facility Offer Letter
   * document (36) from this screen's popup — the draft, then its printed
   * copy. (The Drafted Agreement, 40, is gone in the new flow.)
   */
  private readonly accountManagerDocSeqs = [FO_LTR_DOC_SEQ];

  /** Printed copy of doc 36 replaces the existing doc 36 row. */
  readonly letterReplaceDocSeqs = [FO_LTR_DOC_SEQ];

  @ViewChild('letterDocuments') private letterDocumentsComponent?: Document;

  /** The logged-in user's role code, upper-cased. */
  private get currentRole(): string {
    return currentFoLtrRole();
  }

  /** Account Manager (LNLY_AM) — the only role that uploads/downloads here. */
  get isAccountManager(): boolean {
    return hasFoLtrRole(FO_LTR_ROLES.AM);
  }

  /**
   * Download hook for the offer letter documents popup (Account Manager).
   * Instead of the stored file, doc 36 is generated from its parameters:
   *  - letter 2691 (With AM) + doc 902 → print-fo-ltr/{crdtFoLtrSeq} is
   *    called (doc + letter → 4810, issuance date = today) and the letter is
   *    generated — the AM then uploads the printed copy;
   *  - letter 4810 (Printed) + doc 4810 → generated again, without printing.
   * get-fo-ltr-params/{crdtPrpslSeq} always; terms from
   * fo-ltr/trm-params-fol/{crdtFoLtrSeq} (the VCM snapshot), falling back to
   * get-crdt-trm-params/{crdtPrpslSeq} when the letter has none.
   * Resolves `false` (stored file downloads) when the conditions don't hold.
   */
  letterBeforeDownload = async (row: any): Promise<boolean> => {
    const crdtFoLtrSeq = Number(this.selectedLetter?.crdtFoLtrSeq ?? 0);
    const crdtPrpslSeq = Number(this.selectedLetter?.crdtPrpslSeq ?? 0);
    const letterStatus = Number(this.selectedLetter?.status);
    const docStatus = Number(row?.stsKey);

    if (
      !this.isAccountManager ||
      crdtFoLtrSeq <= 0 ||
      crdtPrpslSeq <= 0 ||
      Number(row?.docTyp ?? FO_LTR_DOC_SEQ) !== FO_LTR_DOC_SEQ
    ) {
      return false;
    }

    const toPrint =
      letterStatus === FO_LTR_STATUS.WITH_AM && docStatus === FO_LTR_STATUS.APPROVED;
    const reprint =
      letterStatus === FO_LTR_STATUS.PRINTED && docStatus === FO_LTR_STATUS.PRINTED;

    if (!toPrint && !reprint) {
      return false;
    }

    try {
      await this.generateOfferLetter(crdtFoLtrSeq, crdtPrpslSeq);
    } catch (error) {
      console.error('Unable to generate offer letter document:', error);
      this.toast.error('FACILITIES.MSG.UNABLE_GENERATE_OFFER_LETTER');
      return true;
    }

    if (toPrint) {
      await this.printOfferLetter(crdtFoLtrSeq);
    }

    return true;
  };

  /** POST /crdt-prpsl/fo-ltr/print-fo-ltr/{crdtFoLtrSeq} — then refreshes the popup and the list. */
  private async printOfferLetter(crdtFoLtrSeq: number): Promise<void> {
    try {
      const response: any = await firstValueFrom(this.creditService.printFoLtr(crdtFoLtrSeq));

      if (response?.__hasError) {
        return;
      }

      if (response?.error) {
        this.toast.error(response.error);
        return;
      }

      this.toast.success(response?.message || 'FACILITIES.MSG.OFFER_LETTER_PRINTED');

      if (this.selectedLetter && Number(this.selectedLetter.crdtFoLtrSeq) === crdtFoLtrSeq) {
        this.selectedLetter = {
          ...this.selectedLetter,
          status: FO_LTR_STATUS.PRINTED,
          statusLabel: this.resolveStatusLabel(FO_LTR_STATUS.PRINTED),
        };
      }

      this.letterDocumentsComponent?.loadDocuments();
      this.loadCreditProposalsForLetter();
    } catch (error: any) {
      console.error('Unable to print offer letter:', error);
      this.toast.error(
        error?.error?.message || error?.error?.error || 'FACILITIES.MSG.UNABLE_PRINT_OFFER_LETTER',
      );
    }
  }

  /**
   * Terms for the letter: the letter's own snapshot (trm-params-fol) when
   * the VCM changed values, otherwise the proposal's live term parameters.
   */
  private letterTermParams$(crdtFoLtrSeq: number, crdtPrpslSeq: number): Observable<any> {
    if (crdtFoLtrSeq <= 0) {
      return this.creditService.getCreditTermParameters(crdtPrpslSeq);
    }

    return this.creditService.getTrmParamsFol(crdtFoLtrSeq).pipe(
      catchError((error) => {
        console.error('Unable to load trm-params-fol:', error);
        return of(null);
      }),
      map((response) => this.unwrapTermParams(response)),
      switchMap((letterParams) =>
        letterParams ? of(letterParams) : this.creditService.getCreditTermParameters(crdtPrpslSeq),
      ),
    );
  }

  /** Builds and downloads the offer letter (.docx); rejects when it can't. */
  private async generateOfferLetter(crdtFoLtrSeq: number, crdtPrpslSeq: number): Promise<void> {
    const { foLtrParams, trmParams, covenants, outcomes } = await firstValueFrom(
      forkJoin({
        foLtrParams: this.creditService.getFacilityOfferLetterParams(crdtPrpslSeq),
        trmParams: this.letterTermParams$(crdtFoLtrSeq, crdtPrpslSeq),
        covenants: this.loadOfferLetterCovenants(crdtPrpslSeq),
        outcomes: this.loadOfferLetterOutcomes(crdtPrpslSeq),
      }),
    );

    await this.offerLetterExport.exportToWord(
      foLtrParams,
      trmParams,
      covenants,
      outcomes,
      `Facility-Offer-Letter-${crdtPrpslSeq}`,
    );
  }

  /**
   * trm-params-fol body in the get-crdt-trm-params shape the offer letter
   * export reads (proposal-level fields + `crdtTrmParamsLst`), or null when
   * it is empty (null, {}, [] or `{ data: null }`). The snapshot comes back
   * as a flat list of term rows (one per repayment type), so the list becomes
   * `crdtTrmParamsLst` and the proposal-level fields are taken from its first row.
   */
  private unwrapTermParams(response: any): any | null {
    // A letter the VCM never changed has no snapshot: the backend may answer
    // 200 with `{ error: 'No record found' }` (flagged `__hasError` by the
    // interceptor). That is "no snapshot", so the proposal's terms are used.
    if (response?.__hasError || (response && !Array.isArray(response) && response.error)) {
      return null;
    }

    const payload = response?.data !== undefined ? response.data : response;

    if (!Array.isArray(payload)) {
      return payload && typeof payload === 'object' && Object.keys(payload).length > 0
        ? payload
        : null;
    }

    const rows = payload.filter(
      (row) => row && typeof row === 'object' && Number(row.delFlg ?? 0) !== 1,
    );

    // The letter's terms come from trm-params-fol only when the VCM changed at
    // least one term type (`chngFlg: true` on any row) — then every row of
    // this response is used. When every type has `chngFlg: false` (the
    // placeholder rows a letter without changes gets), null is returned and
    // the proposal's own terms (get-crdt-trm-params) are used instead.
    const changed = rows.some((row) => row.chngFlg === true);

    if (rows.length === 0 || !changed) {
      return null;
    }

    // Already in the proposal shape (one object carrying the row list).
    if (Array.isArray(rows[0].crdtTrmParamsLst)) {
      return rows[0];
    }

    const first = rows[0];

    return {
      ...first,
      prpsdExposure: first.prpsdExposure ?? first.prpsdExpsr ?? null,
      crdtTrmParamsLst: rows,
    };
  }

  /** Account Manager: limits both the upload dropdown and the popup listing to these types. */
  get letterDocumentAllowedDocSeqs(): number[] {
    return this.isAccountManager ? this.accountManagerDocSeqs : [];
  }

  showLetterDocumentPopup = false;
  selectedLetter: CreditProposalForLetterRow | null = null;

  /** Documents currently listed in the popup, emitted by `app-document`. */
  letterDocuments: any[] = [];

  onLetterDocumentsChange(documents: any[]): void {
    this.letterDocuments = Array.isArray(documents) ? documents : [];
  }

  /**
   * get-fo-ltr-doc `canUpload` (AM at 720 and 4810). There is no submit in
   * the new flow (`canSubmit` is always false): uploading doc 36 at 720
   * sends it to the RO, and uploading the printed copy at 4810 sends it for
   * the RO's acceptance, both automatically.
   */
  letterCanUpload = false;

  onLetterDocFlagsChange(flags: FoLtrDocFlags): void {
    this.letterCanUpload = flags?.canUpload === true;
  }

  /**
   * After an upload in the popup the letter's status moves on (720 → 2658,
   * 4810 → 4811), so the list is reloaded and the popup header follows.
   */
  onLetterDocumentSaved(): void {
    this.loadCreditProposalsForLetter(() => {
      const current = this.creditProposalList.find(
        (item) => item.crdtFoLtrSeq === this.selectedLetter?.crdtFoLtrSeq,
      );

      if (current && this.selectedLetter) {
        this.selectedLetter = { ...this.selectedLetter, ...current };
      }
    });
  }

  /**
   * Offer-letter statuses are not confined to one group — e.g. 720 ("Initiated")
   * lives in ONBOARDING_STATUS (23). `refCdSeq` is unique across groups, so the
   * label is resolved by scanning these status groups in order.
   */
  private readonly statusGroups = [
    // Facility Offer Letter statuses (ref code group 50) come first; the
    // older groups stay as fallbacks for codes not in it.
    PKL_GRP.FOL_STATUS,
    PKL_GRP.ONBOARDING_STATUS,
    PKL_GRP.STATUS,
    PKL_GRP.COMMON_STATUS,
    PKL_GRP.LEADS_STATUS,
    PKL_GRP.PANDDING_STATUS,
  ];

  ngOnInit(): void {
    this.initCreditProposalColumns();
    this.initPendingDraftColumns();
    this.initClientApprovalColumns();
    this.initStageTwoColumns();
    this.initPendingApprovalColumns();
    this.initClientRecommendationColumns();

    // Status labels come from the picklist store; reload the rows once this
    // batch is actually in the store so the labels resolve.
    this.pickListService.loadCodesAsync(this.statusGroups).subscribe({
      next: () => this.loadCreditProposalsForLetter(),
      error: (error: any) => {
        console.error('Unable to load status picklist:', error);

        this.loadCreditProposalsForLetter();
      },
    });
  }

  // ================= LOAD =================
  loadCreditProposalsForLetter(onLoaded?: () => void): void {
    this.creditProposalLoading = true;
    this.creditProposalError = '';

    this.creditService.getCreditProposalsForLetter().subscribe({
      next: (response: any) => {
        // The endpoint returns a single object when only one letter exists
        const rows: CreditProposalForLetterDto[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response
              ? [response]
              : [];

        this.creditProposalList = rows
          .filter((item) => Number(item.delFlg ?? 0) !== 1)
          .map((item) => this.mapCreditProposalToRow(item))
          .sort((first, second) => (second.crdtFoLtrSeq ?? 0) - (first.crdtFoLtrSeq ?? 0));

        this.creditProposalLoading = false;

        onLoaded?.();
      },

      error: (error: any) => {
        console.error('Unable to load offer letters:', error);

        this.creditProposalList = [];
        this.creditProposalError = 'FACILITIES.ERR.LOAD_OFFER_LETTERS';
        this.creditProposalLoading = false;
      },
    });
  }

  /** GET /crdt-prpsl/fo-ltr/aprvd-lst — approved facility offer letters */
  loadApprovedCreditProposalsForLetter(): void {
    this.approvedLettersLoading = true;
    this.approvedLettersError = '';

    this.creditService.getApprovedCreditProposalsForLetter().subscribe({
      next: (response: any) => {
        const rows: CreditProposalForLetterDto[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response
              ? [response]
              : [];

        this.approvedLetterList = rows
          .filter((item) => Number(item.delFlg ?? 0) !== 1)
          .map((item) => this.mapCreditProposalToRow(item))
          .sort((first, second) => (second.crdtFoLtrSeq ?? 0) - (first.crdtFoLtrSeq ?? 0));

        this.approvedLettersLoaded = true;
        this.approvedLettersLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load approved offer letters:', error);

        this.approvedLetterList = [];
        this.approvedLettersError = 'FACILITIES.ERR.LOAD_APPROVED_LETTERS';
        this.approvedLettersLoading = false;
      },
    });
  }

  /** GET /crdt-prpsl/fo-ltr/pending-draft-clnt-lst — clients with draft documents pending the Relationship Officer. */
  /** GET /crdt-prpsl/fo-ltr/ro-acptnc-pndng-clnt-lst */
  loadClientApprovalList(): void {
    this.clientApprovalLoading = true;
    this.clientApprovalError = '';

    this.creditService.getRoAcceptancePendingClientList().subscribe({
      next: (response: any) => {
        const rows: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response
              ? [response]
              : [];

        this.clientApprovalList = rows
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => ({
            ...item,
            crdtFoLtrSeq: item?.crdtFoLtrSeq != null ? Number(item.crdtFoLtrSeq) : null,
            clntNm: item?.clntNm || '-',
            docCnt: item?.docCnt != null ? Number(item.docCnt) : 0,
          }));

        this.clientApprovalLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load client approvals:', error);

        this.clientApprovalList = [];
        this.clientApprovalError = 'FACILITIES.ERR.LOAD_CLIENT_APPROVALS';
        this.clientApprovalLoading = false;
      },
    });
  }

  openClientApprovalDocuments(row: any): void {
    this.clientApprovalClient = row;
    this.loadClientApprovalDocuments();
  }

  backToClientApprovalClients(): void {
    this.clientApprovalClient = null;
    this.clientApprovalDocList = [];
    this.clientApprovalDocsError = '';
  }

  /** GET /crdt-prpsl/fo-ltr/ro-acptnc-pndng-docs?crdtFoLtrSeq= — the open client's documents. */
  loadClientApprovalDocuments(): void {
    const crdtFoLtrSeq = Number(this.clientApprovalClient?.crdtFoLtrSeq ?? 0);

    if (crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_MISSING');
      return;
    }

    this.clientApprovalDocsLoading = true;
    this.clientApprovalDocsError = '';

    this.creditService.getRoAcceptancePendingDocuments(crdtFoLtrSeq).subscribe({
      next: (response: any) => {
        const rows: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response
              ? [response]
              : [];

        this.clientApprovalDocList = rows
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => this.mapPendingDraftToRow(item));

        this.clientApprovalDocsLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load client approval documents:', error);

        this.clientApprovalDocList = [];
        this.clientApprovalDocsError = 'FACILITIES.ERR.LOAD_CLIENT_APPROVAL_DOCS';
        this.clientApprovalDocsLoading = false;
      },
    });
  }

  loadPendingDraftDocuments(): void {
    this.pendingDraftsLoading = true;
    this.pendingDraftsError = '';

    this.creditService.getPendingDraftFoLtrClientList().subscribe({
      next: (response: any) => {
        const rows: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response
              ? [response]
              : [];

        this.pendingDraftList = rows
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => ({
            ...item,
            crdtFoLtrSeq: item?.crdtFoLtrSeq != null ? Number(item.crdtFoLtrSeq) : null,
            clntNm: item?.clntNm || '-',
            docCnt: item?.docCnt != null ? Number(item.docCnt) : 0,
          }));

        this.pendingDraftsLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load pending drafts:', error);

        this.pendingDraftList = [];
        this.pendingDraftsError = 'FACILITIES.ERR.LOAD_PENDING_DRAFTS';
        this.pendingDraftsLoading = false;
      },
    });
  }

  /** readOnly (eye icon) hides the Approve / Reject / Recommendation actions. */
  openPendingDraftDocuments(row: any, readOnly = false): void {
    this.pendingDraftClient = row;
    this.pendingDraftReadOnly = readOnly;
    this.loadPendingDraftClientDocuments();
  }

  backToPendingDraftClients(): void {
    this.pendingDraftClient = null;
    this.pendingDraftReadOnly = false;
    this.pendingDraftDocList = [];
    this.pendingDraftDocsError = '';
  }

  /** GET /crdt-prpsl/fo-ltr/pending-draft-docs?crdtFoLtrSeq= — the open client's draft documents. */
  loadPendingDraftClientDocuments(): void {
    const crdtFoLtrSeq = Number(this.pendingDraftClient?.crdtFoLtrSeq ?? 0);

    if (crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_MISSING');
      return;
    }

    this.pendingDraftDocsLoading = true;
    this.pendingDraftDocsError = '';

    this.creditService.getPendingDraftFoLtrDocuments(crdtFoLtrSeq).subscribe({
      next: (response: any) => {
        const rows: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response
              ? [response]
              : [];

        this.pendingDraftDocList = rows
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => this.mapPendingDraftToRow(item));

        this.pendingDraftDocsLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load pending draft documents:', error);

        this.pendingDraftDocList = [];
        this.pendingDraftDocsError = 'FACILITIES.ERR.LOAD_PENDING_DRAFT_DOCS';
        this.pendingDraftDocsLoading = false;
      },
    });
  }

  /** After a draft action: refresh the client list (doc counts) and the open client's documents. */
  private refreshPendingDrafts(): void {
    this.loadPendingDraftDocuments();

    if (this.pendingDraftClient) {
      this.loadPendingDraftClientDocuments();
    }
  }

  /**
   * Pending Actions (LNLY_AM) client list — the approved letters
   * (aprvd-lst): issue documents, and so the AM's signed copies and
   * re-uploads, only exist once the letter is at 902.
   */
  loadPendingApprovalDocuments(): void {
    this.pendingApprovalLoading = true;
    this.pendingApprovalError = '';

    this.creditService.getApprovedCreditProposalsForLetter().subscribe({
      next: (response: any) => {
        this.pendingApprovalList = (this.unwrapList(response) as CreditProposalForLetterDto[])
          .filter((item) => Number(item.delFlg ?? 0) !== 1)
          .map((item) => this.mapCreditProposalToRow(item))
          .sort((first, second) => (second.crdtFoLtrSeq ?? 0) - (first.crdtFoLtrSeq ?? 0));

        this.pendingApprovalLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load pending actions clients:', error);

        this.pendingApprovalList = [];
        this.pendingApprovalError = 'FACILITIES.ERR.LOAD_PENDING_APPROVAL';
        this.pendingApprovalLoading = false;
      },
    });
  }

  /** readOnly (eye icon) hides the upload / re-upload actions. */
  openPendingApprovalDocuments(row: any, readOnly = false): void {
    this.pendingApprovalClient = row;
    this.pendingApprovalReadOnly = readOnly;
    this.loadPendingApprovalClientDocuments();
  }

  backToPendingApprovalClients(): void {
    this.pendingApprovalClient = null;
    this.pendingApprovalReadOnly = false;
    this.pendingApprovalDocList = [];
    this.pendingApprovalDocsError = '';
  }

  /** GET /crdt-prpsl/fo-ltr/am-doc-pndng-lst?crdtFoLtrSeq= — 4813 (signed copy due) and 4814 (recommendation). */
  loadPendingApprovalClientDocuments(): void {
    const crdtFoLtrSeq = Number(this.pendingApprovalClient?.crdtFoLtrSeq ?? 0);

    if (crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_MISSING');
      return;
    }

    this.pendingApprovalDocsLoading = true;
    this.pendingApprovalDocsError = '';

    this.creditService.getAmDocPendingFoLtrDocuments(crdtFoLtrSeq).subscribe({
      next: (response: any) => {
        this.pendingApprovalDocList = this.unwrapList(response)
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => this.mapPendingDraftToRow(item));

        this.pendingApprovalDocsLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load pending action documents:', error);

        this.pendingApprovalDocList = [];
        this.pendingApprovalDocsError = 'FACILITIES.ERR.LOAD_PENDING_APPROVAL_DOCS';
        this.pendingApprovalDocsLoading = false;
      },
    });
  }

  /** After an upload: refresh the open client's documents. */
  private refreshPendingApprovals(): void {
    if (this.pendingApprovalClient) {
      this.loadPendingApprovalClientDocuments();
    }
  }

  /** GET /crdt-prpsl/fo-ltr/rcmnd-required-lst — documents awaiting the VCM's recommendation. */
  loadClientRecommendations(): void {
    this.clientRecommendationsLoading = true;
    this.clientRecommendationsError = '';

    this.creditService.getClientRecommendationList().subscribe({
      next: (response: any) => {
        const rows: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response
              ? [response]
              : [];

        this.clientRecommendationList = rows
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => this.mapPendingDraftToRow(item));

        this.clientRecommendationsLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load client recommendations:', error);

        this.clientRecommendationList = [];
        this.clientRecommendationsError = 'FACILITIES.ERR.LOAD_CLIENT_RECOMMENDATIONS';
        this.clientRecommendationsLoading = false;
      },
    });
  }

  // ================= EXTENSION LIST =================
  extensionList: any[] = [];
  extensionLoading = false;
  extensionError = '';

  /** One row per client, like the Pending Drafts client list. */
  extensionColumns: TableColumn[] = [
    {
      key: 'crdtFoLtrSeq',
      label: 'FACILITIES.OFFER_LETTER_ID',
      type: 'text',
    },
    {
      key: 'clntNm',
      label: 'FACILITIES.INSTITUTION_NAME',
      type: 'link',
      onClick: (row: any) => this.openExtensionDocuments(row),
    },
    {
      key: 'docCnt',
      label: 'FACILITIES.DOCUMENT_COUNT',
      type: 'text',
    },
  ];

  /** Client row whose documents are open; null shows the client list. */
  extensionClient: any = null;
  extensionDocList: any[] = [];
  extensionDocsLoading = false;
  extensionDocsError = '';

  extensionDocColumns: TableColumn[] = [
    {
      key: 'crdtFoLtrSeq',
      label: 'FACILITIES.OFFER_LETTER_ID',
      type: 'text',
    },
    {
      key: 'docNm',
      label: 'FACILITIES.DOCUMENT_NAME',
      type: 'text',
    },
    {
      key: 'cmmtee',
      label: 'FACILITIES.COMMITTEE',
      type: 'text',
    },
    {
      key: 'statusLabel',
      label: 'COMMON.STATUS',
      type: 'text',
    },
    {
      key: 'rqstCmmnt',
      label: 'FACILITIES.REQUEST_COMMENT',
      type: 'text',
    },
    {
      key: 'dcsnCmmnt',
      label: 'FACILITIES.DECISION_COMMENT',
      type: 'text',
    },
  ];

  extensionDocActions: TableAction[] = [
    {
      icon: 'images/send.png',
      tooltip: 'FACILITIES.REQUEST_EXTENSION',
      class:
        'w-8 h-8 flex items-center justify-center rounded-full hover:bg-emerald-50 transition-colors',
      // The table applies iconSize as the <img> class.
      iconSize: 'w-4 h-4',
      onClick: (row: any) => this.openExtensionRequestPopup(row),
    },
  ];

  // ================= EXTENSION REQUEST =================
  showExtensionRequestPopup = false;
  extensionRequestFields: DynamicFieldConfig[] = [];
  extensionRequestFormData: any = {};
  private extensionRequestRow: any = null;

  openExtensionRequestPopup(row: any): void {
    this.extensionRequestRow = row;

    this.extensionRequestFormData = { cmmnt: '' };

    this.extensionRequestFields = [
      {
        key: 'cmmnt',
        label: 'FACILITIES.COMMENT',
        type: 'text',
        validations: {
          required: true,
        },
      },
    ];

    this.showExtensionRequestPopup = true;
  }

  /** POST /crdt-prpsl/fo-ltr/xtnsn-request */
  handleExtensionRequestSave = (form: any) => {
    const crdtFoLtrSeq = Number(this.extensionRequestRow?.crdtFoLtrSeq ?? 0);
    const docSeq = Number(this.extensionRequestRow?.docSeq ?? 0);

    if (crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_MISSING');
      return;
    }

    if (docSeq <= 0) {
      this.toast.error('FACILITIES.MSG.DOC_SEQ_MISSING');
      return;
    }

    this.creditService
      .requestFoLtrExtension({
        crdtFoLtrSeq,
        docSeq,
        cmmnt: form?.cmmnt || '',
      })
      .subscribe({
        next: (response: any) => {
          if (response?.error) {
            this.toast.error(response.error);
            return;
          }

          this.toast.success(response?.message || 'FACILITIES.MSG.EXTENSION_REQUESTED');

          this.extensionRequestRow = null;

          this.loadExtensionClientDocuments();
        },

        error: (error: any) => {
          console.error('Unable to request extension:', error);

          this.toast.error(
            error?.error?.message || error?.error?.error || 'FACILITIES.MSG.UNABLE_REQUEST_EXTENSION',
          );
        },
      });
  };

  extensionReadOnly = false;

  /** Client list: the eye opens that client's documents read-only. */
  extensionClientActions: TableAction[] = [
    {
      icon: this.viewIcon,
      tooltip: 'FACILITIES.VIEW_DOCUMENTS',
      class: 'action-view',
      onClick: (row: any) => this.openExtensionDocuments(row, true),
      visible: (row: any) => Number(row?.crdtFoLtrSeq) > 0,
    },
  ];

  /** readOnly (eye icon) hides the Request Extension action. */
  openExtensionDocuments(row: any, readOnly = false): void {
    this.extensionClient = row;
    this.extensionReadOnly = readOnly;
    this.loadExtensionClientDocuments();
  }

  backToExtensionClients(): void {
    this.extensionClient = null;
    this.extensionReadOnly = false;
    this.extensionDocList = [];
    this.extensionDocsError = '';
  }

  /** GET /crdt-prpsl/fo-ltr/mrc-brc-required-docs?crdtFoLtrSeq= — the open client's documents. */
  loadExtensionClientDocuments(): void {
    const crdtFoLtrSeq = Number(this.extensionClient?.crdtFoLtrSeq ?? 0);

    if (crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_MISSING');
      return;
    }

    this.extensionDocsLoading = true;
    this.extensionDocsError = '';

    this.creditService.getFoLtrExtensionDocuments(crdtFoLtrSeq).subscribe({
      next: (response: any) => {
        const rows: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response
              ? [response]
              : [];

        this.extensionDocList = this.mapExtensionDocRows(rows);

        this.extensionDocsLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load extension documents:', error);

        this.extensionDocList = [];
        this.extensionDocsError = 'FACILITIES.ERR.LOAD_EXTENSION_DOCS';
        this.extensionDocsLoading = false;
      },
    });
  }

  /** GET /crdt-prpsl/fo-ltr/mrc-brc-required-clnt-lst — clients for the Extention List tab. */
  loadExtensionList(): void {
    this.extensionLoading = true;
    this.extensionError = '';

    this.creditService.getFoLtrExtensionList().subscribe({
      next: (response: any) => {
        const rows: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response
              ? [response]
              : [];

        this.extensionList = rows
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => ({
            ...item,
            crdtFoLtrSeq: item?.crdtFoLtrSeq != null ? Number(item.crdtFoLtrSeq) : null,
            clntNm: item?.clntNm || '-',
            docCnt: item?.docCnt != null ? Number(item.docCnt) : 0,
          }));

        this.extensionLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load extension list:', error);

        this.extensionList = [];
        this.extensionError = 'FACILITIES.ERR.LOAD_EXTENSION_LIST';
        this.extensionLoading = false;
      },
    });
  }

  /** Extension document rows (Extention List and Pending Extentions). */
  private mapExtensionDocRows(rows: any[]): any[] {
    // Status labels come from ref code groups 26 (PANDDING_STATUS) and 23 (ONBOARDING_STATUS).
    const statusOptions = [
      ...this.pickListService.getOptionsSync(PKL_GRP.FOL_STATUS),
      ...this.pickListService.getOptionsSync(PKL_GRP.PANDDING_STATUS),
      ...this.pickListService.getOptionsSync(PKL_GRP.ONBOARDING_STATUS),
    ];

    return rows
      .filter((item) => Number(item?.delFlg ?? 0) !== 1)
      .map((item) => {
        const status = item?.stsKey != null ? Number(item.stsKey) : null;

        return {
          ...item,
          crdtFoLtrSeq: item?.crdtFoLtrSeq != null ? Number(item.crdtFoLtrSeq) : null,
          docNm: item?.docNm || '-',
          cmmtee: item?.cmmtee || '-',
          // xtnsn-pending-lst / mrc-brc-required-docs return the comments.
          rqstCmmnt: item?.rqstCmmnt || '-',
          dcsnCmmnt: item?.dcsnCmmnt || '-',
          statusLabel:
            status == null
              ? '-'
              : (statusOptions.find((option) => Number(option.value) === status)?.label ??
                String(status)),
        };
      });
  }

  // ================= PENDING EXTENSIONS =================
  pendingExtensionList: any[] = [];
  pendingExtensionLoading = false;
  pendingExtensionError = '';

  /** Client row whose documents are open; null shows the client list. */
  pendingExtensionClient: any = null;
  pendingExtensionDocList: any[] = [];
  pendingExtensionDocsLoading = false;
  pendingExtensionDocsError = '';

  pendingExtensionDocActions: TableAction[] = [
    {
      icon: 'images/Tick-2.png',
      tooltip: 'COMMON.APPROVE',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-green-100 rounded-full',
      iconSize: 'w-5 h-5',
      onClick: (row: any) => this.openGrantExtensionPopup(row, 'approve'),
    },
    {
      icon: 'images/cancel.png',
      tooltip: 'COMMON.REJECT',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-red-100 rounded-full',
      iconSize: 'w-5 h-5',
      onClick: (row: any) => this.openGrantExtensionPopup(row, 'reject'),
    },
  ];

  // ================= APPROVE / REJECT EXTENSION =================
  /** Approve and reject share one popup (Till Date + Comment) and one payload. */
  showGrantExtensionPopup = false;
  grantExtensionMode: 'approve' | 'reject' = 'approve';
  grantExtensionFields: DynamicFieldConfig[] = [];
  grantExtensionFormData: any = {};
  private grantExtensionSeq: number | null = null;

  openGrantExtensionPopup(row: any, mode: 'approve' | 'reject'): void {
    const crdtFoLtrDocXtnsnSeq = Number(row?.crdtFoLtrDocXtnsnSeq ?? 0);

    if (crdtFoLtrDocXtnsnSeq <= 0) {
      this.toast.error('FACILITIES.MSG.EXTENSION_SEQ_MISSING');
      return;
    }

    this.grantExtensionSeq = crdtFoLtrDocXtnsnSeq;
    this.grantExtensionMode = mode;

    this.grantExtensionFormData = { tillDt: '', cmmnt: '' };

    this.grantExtensionFields = [
      {
        key: 'tillDt',
        label: 'FACILITIES.TILL_DATE',
        type: 'date',
        validations: {
          required: true,
          futureDateOnly: true,
        },
      },
      {
        key: 'cmmnt',
        label: 'FACILITIES.COMMENT',
        type: 'text',
        validations: {
          required: true,
        },
      },
    ];

    this.showGrantExtensionPopup = true;
  }

  /** POST /crdt-prpsl/fo-ltr/xtnsn-grant | /xtnsn-reject */
  handleGrantExtensionSave = (form: any) => {
    const crdtFoLtrDocXtnsnSeq = Number(this.grantExtensionSeq ?? 0);

    if (crdtFoLtrDocXtnsnSeq <= 0) {
      this.toast.error('FACILITIES.MSG.EXTENSION_SEQ_MISSING');
      return;
    }

    const isApprove = this.grantExtensionMode === 'approve';

    const payload = {
      crdtFoLtrDocXtnsnSeq,
      tillDt: form?.tillDt || '',
      cmmnt: form?.cmmnt || '',
    };

    const request$ = isApprove
      ? this.creditService.grantFoLtrExtension(payload)
      : this.creditService.rejectFoLtrExtension(payload);

    request$.subscribe({
      next: (response: any) => {
        if (response?.error) {
          this.toast.error(response.error);
          return;
        }

        this.toast.success(
          response?.message ||
            (isApprove ? 'FACILITIES.MSG.EXTENSION_APPROVED' : 'FACILITIES.MSG.EXTENSION_REJECTED'),
        );

        this.grantExtensionSeq = null;

        this.loadPendingExtensionList();
        this.loadPendingExtensionClientDocuments();
      },

      error: (error: any) => {
        console.error('Extension action failed:', error);

        this.toast.error(
          error?.error?.message ||
            error?.error?.error ||
            (isApprove ? 'FACILITIES.MSG.UNABLE_APPROVE_EXTENSION' : 'FACILITIES.MSG.UNABLE_REJECT_EXTENSION'),
        );
      },
    });
  };

  pendingExtensionReadOnly = false;

  /** Client list: the eye opens that client's pending extension documents read-only. */
  pendingExtensionClientActions: TableAction[] = [
    {
      icon: this.viewIcon,
      tooltip: 'FACILITIES.VIEW_DOCUMENTS',
      class: 'action-view',
      onClick: (row: any) => this.openPendingExtensionDocuments(row, true),
      visible: (row: any) => Number(row?.crdtFoLtrSeq) > 0,
    },
  ];

  /** readOnly (eye icon) hides the Approve / Reject actions. */
  openPendingExtensionDocuments(row: any, readOnly = false): void {
    this.pendingExtensionClient = row;
    this.pendingExtensionReadOnly = readOnly;
    this.loadPendingExtensionClientDocuments();
  }

  backToPendingExtensionClients(): void {
    this.pendingExtensionClient = null;
    this.pendingExtensionReadOnly = false;
    this.pendingExtensionDocList = [];
    this.pendingExtensionDocsError = '';
  }

  /** GET /crdt-prpsl/fo-ltr/xtnsn-pending-lst?crdtFoLtrSeq= — the open client's pending extension documents. */
  loadPendingExtensionClientDocuments(): void {
    const crdtFoLtrSeq = Number(this.pendingExtensionClient?.crdtFoLtrSeq ?? 0);

    if (crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_MISSING');
      return;
    }

    this.pendingExtensionDocsLoading = true;
    this.pendingExtensionDocsError = '';

    this.creditService.getFoLtrPendingExtensionDocuments(crdtFoLtrSeq).subscribe({
      next: (response: any) => {
        const rows: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response
              ? [response]
              : [];

        this.pendingExtensionDocList = this.mapExtensionDocRows(rows);

        this.pendingExtensionDocsLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load pending extension documents:', error);

        this.pendingExtensionDocList = [];
        this.pendingExtensionDocsError = 'FACILITIES.ERR.LOAD_PENDING_EXTENSION_DOCS';
        this.pendingExtensionDocsLoading = false;
      },
    });
  }

  /** Same client rows as the Extention List. */
  pendingExtensionColumns: TableColumn[] = [
    {
      key: 'crdtFoLtrSeq',
      label: 'FACILITIES.OFFER_LETTER_ID',
      type: 'text',
    },
    {
      key: 'clntNm',
      label: 'FACILITIES.INSTITUTION_NAME',
      type: 'link',
      onClick: (row: any) => this.openPendingExtensionDocuments(row),
    },
    {
      key: 'docCnt',
      label: 'FACILITIES.DOCUMENT_COUNT',
      type: 'text',
    },
  ];

  /** GET /crdt-prpsl/fo-ltr/xtnsn-pending-clnt-lst — clients with extension requests pending. */
  loadPendingExtensionList(): void {
    this.pendingExtensionLoading = true;
    this.pendingExtensionError = '';

    this.creditService.getFoLtrPendingExtensionList().subscribe({
      next: (response: any) => {
        const rows: any[] = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
            ? response.data
            : response
              ? [response]
              : [];

        this.pendingExtensionList = rows
          .filter((item) => Number(item?.delFlg ?? 0) !== 1)
          .map((item) => ({
            ...item,
            crdtFoLtrSeq: item?.crdtFoLtrSeq != null ? Number(item.crdtFoLtrSeq) : null,
            clntNm: item?.clntNm || '-',
            docCnt: item?.docCnt != null ? Number(item.docCnt) : 0,
          }));

        this.pendingExtensionLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load pending extensions:', error);

        this.pendingExtensionList = [];
        this.pendingExtensionError = 'FACILITIES.ERR.LOAD_PENDING_EXTENSIONS';
        this.pendingExtensionLoading = false;
      },
    });
  }

  private mapPendingDraftToRow(item: any): any {
    const status = item?.stsKey != null ? Number(item.stsKey) : null;

    return {
      ...item,

      crdtFoLtrSeq: item?.crdtFoLtrSeq != null ? Number(item.crdtFoLtrSeq) : null,
      clntNm: item?.clntNm || '-',
      docNm: item?.docNm || '-',
      statusLabel: this.resolveStatusLabel(status),
      uploadDt: item?.uploadDt ?? null,
      copyLabel: this.translate.instant(
        Number(item?.sgndCpyFlg ?? 0) === 1 ? 'FACILITIES.SIGNED_COPY' : 'FACILITIES.ORIGINAL_COPY',
      ),
    };
  }

  private mapCreditProposalToRow(item: CreditProposalForLetterDto): CreditProposalForLetterRow {
    const status = item.stsKey != null ? Number(item.stsKey) : null;

    return {
      crdtFoLtrSeq: item.crdtFoLtrSeq != null ? Number(item.crdtFoLtrSeq) : null,
      crdtPrpslSeq: item.crdtPrpslSeq != null ? Number(item.crdtPrpslSeq) : null,
      clntNm: item.clntNm || '-',
      status: status,
      statusLabel: this.resolveStatusLabel(status),
      crtdBy: item.crtdBy || '-',
      crtdDt: item.crtdDt ?? null,
      lastUpdBy: item.lastUpdBy || '-',
      lastUpdDt: item.lastUpdDt ?? null,
    };
  }

  /** Falls back to the raw code when the status is in none of the status groups. */
  private resolveStatusLabel(status: number | null): string {
    if (status == null) {
      return '-';
    }

    for (const group of this.statusGroups) {
      const option = this.pickListService
        .getOptionsSync(group)
        .find((item) => Number(item.value) === status);

      if (option) {
        return option.label;
      }
    }

    // The FOL new-flow codes (4810–4816) may not be in the picklists yet.
    const fallbackKey = FO_LTR_STATUS_LABEL_KEYS[status];

    return fallbackKey ? this.translate.instant(fallbackKey) : String(status);
  }

  // ================= TABLE CONFIG =================
  /**
   * Columns for the offer-letter *document* lists (rows from
   * mapPendingDraftToRow); callers supply the trailing date column.
   */
  private buildFoLtrDocumentColumns(dateColumn: TableColumn): TableColumn[] {
    return [
      {
        key: 'crdtFoLtrSeq',
        label: 'FACILITIES.OFFER_LETTER_ID',
        type: 'text',
      },
      {
        key: 'clntNm',
        label: 'FACILITIES.INSTITUTION_NAME',
        type: 'text',
      },
      {
        key: 'docNm',
        label: 'FACILITIES.DOCUMENT_NAME',
        type: 'link',
        onClick: (row: any) => this.downloadFile(row),
      },
      {
        key: 'statusLabel',
        label: 'COMMON.STATUS',
        type: 'text',
      },
      dateColumn,
    ];
  }

  /** Pending Drafts lists one row per client (pending-draft-clnt-lst), not per document. */
  private initPendingDraftColumns(): void {
    this.pendingDraftColumns = [
      {
        key: 'crdtFoLtrSeq',
        label: 'FACILITIES.OFFER_LETTER_ID',
        type: 'text',
      },
      {
        key: 'clntNm',
        label: 'FACILITIES.INSTITUTION_NAME',
        type: 'link',
        onClick: (row: any) => this.openPendingDraftDocuments(row),
      },
      {
        key: 'docCnt',
        label: 'FACILITIES.TOTAL_DOCUMENTS',
        type: 'text',
      },
    ];

    this.pendingDraftDocColumns = this.buildFoLtrDocumentColumns({
      key: 'uploadDt',
      label: 'FACILITIES.UPLOAD_DATE',
      type: 'date',
    });
  }

  /** Client Approval lists one row per client (ro-acptnc-pndng-clnt-lst). */
  private initClientApprovalColumns(): void {
    this.clientApprovalColumns = [
      {
        key: 'crdtFoLtrSeq',
        label: 'FACILITIES.OFFER_LETTER_ID',
        type: 'text',
      },
      {
        key: 'clntNm',
        label: 'FACILITIES.INSTITUTION_NAME',
        type: 'link',
        onClick: (row: any) => this.openClientApprovalDocuments(row),
      },
      {
        key: 'docCnt',
        label: 'FACILITIES.TOTAL_DOCUMENTS',
        type: 'text',
      },
    ];

    this.clientApprovalDocColumns = this.buildFoLtrDocumentColumns({
      key: 'uploadDt',
      label: 'FACILITIES.UPLOAD_DATE',
      type: 'date',
    });
  }

  /** Pending Review (RO / TL) and SAC Review document lists — issue documents and signed copies. */
  private initStageTwoColumns(): void {
    const columns = (): TableColumn[] => [
      ...this.buildFoLtrDocumentColumns({
        key: 'uploadDt',
        label: 'FACILITIES.UPLOAD_DATE',
        type: 'date',
      }),
      { key: 'copyLabel', label: 'FACILITIES.COPY_TYPE', type: 'text' },
    ];

    this.roReviewDocColumns = columns();
    this.sacReviewDocColumns = columns();
  }

  /** Pending Actions lists one row per approved letter (aprvd-lst), not per document. */
  private initPendingApprovalColumns(): void {
    this.pendingApprovalColumns = [
      {
        key: 'crdtFoLtrSeq',
        label: 'FACILITIES.OFFER_LETTER_ID',
        type: 'text',
      },
      {
        key: 'clntNm',
        label: 'FACILITIES.INSTITUTION_NAME',
        type: 'link',
        onClick: (row: any) => this.openPendingApprovalDocuments(row),
      },
      {
        key: 'statusLabel',
        label: 'COMMON.STATUS',
        type: 'text',
      },
    ];

    this.pendingApprovalDocColumns = [
      ...this.buildFoLtrDocumentColumns({
        key: 'uploadDt',
        label: 'FACILITIES.UPLOAD_DATE',
        type: 'date',
      }),
      { key: 'copyLabel', label: 'FACILITIES.COPY_TYPE', type: 'text' },
    ];
  }

  /** Client list: the eye opens that client's pending-approval documents read-only. */
  pendingApprovalClientActions: TableAction[] = [
    {
      icon: this.viewIcon,
      tooltip: 'FACILITIES.VIEW_DOCUMENTS',
      class: 'action-view',
      onClick: (row: any) => this.openPendingApprovalDocuments(row, true),
      visible: (row: any) => Number(row?.crdtFoLtrSeq) > 0,
    },
  ];

  /** Client list: the eye opens that client's draft documents read-only. */
  pendingDraftClientActions: TableAction[] = [
    {
      icon: this.viewIcon,
      tooltip: 'FACILITIES.VIEW_DRAFT_DOCUMENTS',
      class: 'action-view',
      onClick: (row: any) => this.openPendingDraftDocuments(row, true),
      visible: (row: any) => Number(row?.crdtFoLtrSeq) > 0,
    },
  ];

  /**
   * Client Approval documents (doc 36 at 4811): the RO uploads the copy the
   * client accepted — letter → 902. The Team Lead only views.
   */
  clientApprovalDocActions: TableAction[] = [
    {
      icon: 'images/upload.png',
      tooltip: 'FACILITIES.UPLOAD_ACCEPTED_COPY',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openRowUploadPopup(row, 'clientApproval'),
      visible: () => this.isRelationshipOfficer,
    },
  ];

  // ================= LEGAL OPINION (LNLY_AM) =================
  /**
   * Legal Opinion tab: the offer letters pending a legal opinion
   * (`GET /crdt-prpsl/fo-ltr/pndng-lgl-opnn-lst`). A row's upload icon opens
   * the inline form — Document Type fixed to document type 51 of the offer
   * letter category (`GET /setup/get-all-docs/13`), a required Comment and the
   * file uploader — which saves against that row's letter.
   */
  private readonly LEGAL_OPINION_DOC_SEQ = 51;

  legalOpinionList: any[] = [];
  legalOpinionListLoading = false;
  legalOpinionListError = '';

  /** The letter whose legal opinion is being uploaded; null shows the list. */
  legalOpinionRow: any = null;

  legalOpinionColumns: TableColumn[] = [
    { key: 'crdtFoLtrSeq', label: 'FACILITIES.OFFER_LETTER_ID', type: 'text' },
    { key: 'clntNm', label: 'FACILITIES.INSTITUTION_NAME', type: 'text' },
  ];

  legalOpinionActions: TableAction[] = [
    {
      icon: 'images/edit-icon.png',
      tooltip: 'COMMON.EDIT',
      class: 'w-6 h-6 flex items-center justify-center rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openLegalOpinionForm(row),
    },
  ];

  loadLegalOpinionList(): void {
    this.legalOpinionListLoading = true;
    this.legalOpinionListError = '';

    this.creditService.getPendingLegalOpinionList().subscribe({
      next: (response: any) => {
        this.legalOpinionList = this.mapClientRows(this.unwrapList(response));
        this.legalOpinionListLoading = false;
      },

      error: (error: any) => {
        console.error('Unable to load pending legal opinions:', error);

        this.legalOpinionList = [];
        this.legalOpinionListError = 'FACILITIES.ERR.LOAD_LEGAL_OPINION';
        this.legalOpinionListLoading = false;
      },
    });
  }

  openLegalOpinionForm(row: any): void {
    this.legalOpinionRow = row;
    this.legalOpinionFile = null;
    this.legalOpinionFileError = '';
    this.legalOpinionComment = '';
    this.buildLegalOpinionForm();
  }

  backToLegalOpinionList(): void {
    this.legalOpinionRow = null;
    this.legalOpinionFile = null;
    this.legalOpinionFileError = '';
    this.legalOpinionComment = '';
  }

  @ViewChild('legalOpinionForm') private legalOpinionForm?: DynamicFormComponent;

  legalOpinionDocType: { label: string; value: number } | null = null;
  legalOpinionLoading = false;
  legalOpinionError = '';

  legalOpinionFields: DynamicFieldConfig[] = [];
  legalOpinionFormData: any = {};
  legalOpinionFile: File | null = null;
  legalOpinionFileError = '';
  legalOpinionSaving = false;

  // Comment — same rich text editor as the credit proposal's Company Introduction.
  legalOpinionComment = '';

  private buildLegalOpinionForm(): void {
    const docType = this.legalOpinionDocType ?? {
      label: String(this.LEGAL_OPINION_DOC_SEQ),
      value: this.LEGAL_OPINION_DOC_SEQ,
    };

    this.legalOpinionFields = [
      {
        key: 'docTyp',
        label: 'FACILITIES.TAB_LEGAL_OPINION',
        type: 'select',
        options$: [docType],
        disabled: true,
      },
      {
        // Any past or future date is allowed — no date validation.
        key: 'fcltyEffctveDt',
        label: 'FACILITIES.FACILITY_EFFECTIVE_DATE',
        type: 'date',
      },
    ];

    this.legalOpinionFormData = {
      docTyp: docType.value,
      fcltyEffctveDt: null,
    };
  }

  private loadLegalOpinionDocType(): void {
    this.buildLegalOpinionForm();

    this.legalOpinionLoading = true;
    this.legalOpinionError = '';

    this.setup.getAllDocumentByCategry(MODULE_SEQ.FACILITY_OFFER_LETTER).subscribe({
      next: (res: any) => {
        const list: any[] = Array.isArray(res) ? res : res?.data || [];
        const doc = list.find((item) => Number(item?.docSeq) === this.LEGAL_OPINION_DOC_SEQ);

        this.legalOpinionDocType = doc
          ? { label: doc.docNm, value: this.LEGAL_OPINION_DOC_SEQ }
          : null;
        this.legalOpinionLoading = false;
        this.buildLegalOpinionForm();
      },
      error: (error) => {
        console.error('Unable to load document types:', error);

        this.legalOpinionDocType = null;
        this.legalOpinionLoading = false;
        this.legalOpinionError = 'DOCUMENT.FAILED_TO_LOAD_DOCUMENT_TYPES';
      },
    });
  }

  onLegalOpinionFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    this.legalOpinionFile = input.files?.[0] ?? null;
    this.legalOpinionFileError = '';

    // Lets the same file be picked again after a cancel.
    input.value = '';
  }

  /**
   * POST /crdt-prpsl/fo-ltr/save-doc — same payload as the documents screens'
   * upload, against the selected row's letter, with the Comment as `cmnt`.
   * A new record, so `crdtFoLtrDocSeq` is left out.
   */
  submitLegalOpinion(): void {
    const form = this.legalOpinionForm?.form;
    const crdtFoLtrSeq = Number(this.legalOpinionRow?.crdtFoLtrSeq ?? 0);

    if (crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_MISSING');
      return;
    }

    if (!this.legalOpinionFile) {
      this.legalOpinionFileError = 'DOCUMENT.FILE_REQUIRED';
    }

    if (form?.invalid) {
      form.markAllAsTouched();
    }

    if (!this.legalOpinionFile || form?.invalid || this.legalOpinionSaving) {
      return;
    }

    if (!hasRichTextContent(this.legalOpinionComment)) {
      this.toast.error('FACILITIES.ERR.LEGAL_OPINION_COMMENT_REQUIRED');
      return;
    }

    const today = new Date().toISOString().slice(0, 10);
    const formData = new FormData();

    formData.append('crdtFoLtrSeq', String(crdtFoLtrSeq));
    formData.append('docSeq', String(this.LEGAL_OPINION_DOC_SEQ));
    formData.append('docDesc', '');
    // save-doc rejects the request without aprvngDt, so it is always sent.
    formData.append('aprvngDt', today);
    formData.append('uploadDt', today);
    formData.append('cmnt', richTextForSave(this.legalOpinionComment));

    const fcltyEffctveDt = form?.getRawValue()?.fcltyEffctveDt;

    if (fcltyEffctveDt) {
      formData.append('fcltyEffctveDt', String(fcltyEffctveDt));
    }

    formData.append('delFlg', '0');
    formData.append('file', this.legalOpinionFile);

    this.legalOpinionSaving = true;

    this.creditService.saveFoLtrDoc(formData).subscribe({
      next: (response: any) => {
        this.legalOpinionSaving = false;

        // save-doc can return 200 with an error body; the interceptor already toasted it.
        if (response?.__hasError) {
          return;
        }

        if (response?.error) {
          this.toast.error(response.error);
          return;
        }

        this.toast.success('DOCUMENT.SAVED_SUCCESSFULLY');

        this.backToLegalOpinionList();
        this.loadLegalOpinionList();
      },

      error: (error: any) => {
        console.error('Unable to save the legal opinion:', error);

        this.legalOpinionSaving = false;
        this.toast.error(error?.error?.message || error?.error?.error || 'DOCUMENT.SAVE_FAILED');
      },
    });
  }

  // ================= ROW UPLOAD (Client Approval / AM signed copy / AM re-upload) =================
  /**
   * Same upload popup as the documents screens (Document Type, Document
   * Name, click-to-upload box). The type and name are the row's own and
   * read-only. Modes:
   *  - clientApproval — RO uploads the accepted copy of doc 36 (no dates);
   *  - signedCopy    — AM uploads the signed copy of a 4813 issue document
   *                    (sgndCpyFlg=1, new row linked to it via prntDocSeq);
   *  - reupload      — AM re-uploads a 4814 document after the RO's
   *                    recommendation (the same row; a signed copy stays one).
   * Issue documents need the Soft Copy date (Hard Copy optional); an LOH
   * signed copy also needs the Charge Type (the backend keeps a charge row).
   */
  @ViewChild('rowUploadForm') private rowUploadForm?: DynamicFormComponent;

  showRowUploadPopup = false;
  rowUploadMode: 'clientApproval' | 'signedCopy' | 'reupload' = 'clientApproval';
  rowUploadFields: DynamicFieldConfig[] = [];
  rowUploadFormData: any = {};
  rowUploadFile: File | null = null;
  rowUploadError = '';
  rowUploadSaving = false;
  private rowUploadRow: any = null;

  /** Same file types the documents screens accept. */
  readonly rowUploadAccept = '.pdf,.doc,.docx';

  get rowUploadTitle(): string {
    switch (this.rowUploadMode) {
      case 'signedCopy':
        return 'FACILITIES.UPLOAD_SIGNED_COPY';
      case 'reupload':
        return 'FACILITIES.REUPLOAD_DOCUMENT';
      default:
        return 'FACILITIES.UPLOAD_ACCEPTED_COPY';
    }
  }

  /** The upload is (or replaces) a signed copy. */
  private isSignedCopyUpload(mode: string, row: any): boolean {
    return mode === 'signedCopy' || (mode === 'reupload' && Number(row?.sgndCpyFlg ?? 0) === 1);
  }

  openRowUploadPopup(row: any, mode: 'clientApproval' | 'signedCopy' | 'reupload'): void {
    this.rowUploadRow = row;
    this.rowUploadMode = mode;
    this.rowUploadFile = null;
    this.rowUploadError = '';

    const isIssueDocUpload = mode !== 'clientApproval';
    const needsChargeType =
      this.isSignedCopyUpload(mode, row) && Number(row?.docSeq) === FO_LTR_LOH_DOC_SEQ;

    this.rowUploadFields = [
      {
        key: 'docTyp',
        label: 'DOCUMENT.DOCUMENT_TYPE',
        type: 'select',
        options$: [{ label: row?.docNm || '-', value: row?.docSeq }],
        disabled: true,
      },
      {
        key: 'docNm',
        label: 'DOCUMENT.DOCUMENT_NAME',
        type: 'text',
        disabled: true,
      },
      ...(isIssueDocUpload
        ? ([
            // Past and current dates only (no future dates).
            {
              key: 'sftCpyDt',
              label: 'DOCUMENT.SOFT_COPY_DATE',
              type: 'date',
              validations: { required: true, noFuturDate: true },
            },
            {
              key: 'hrdCpyDt',
              label: 'DOCUMENT.HARD_COPY_DATE',
              type: 'date',
              validations: { noFuturDate: true },
            },
          ] as DynamicFieldConfig[])
        : []),
      // Stage 4: the scanned final (signed) document records the agreement date.
      ...(this.isSignedCopyUpload(mode, row)
        ? ([
            {
              key: 'signingDt',
              label: 'FACILITIES.AGREEMENT_DATE',
              type: 'date',
              validations: { required: true, noFuturDate: true },
            },
          ] as DynamicFieldConfig[])
        : []),
      ...(needsChargeType
        ? ([
            {
              key: 'chrgTyp',
              label: 'DOCUMENT.CHARGE_TYPE',
              type: 'select',
              options$: this.pickListService.getOptions(PKL_GRP.CHARGE_TYPE),
              validations: { required: true },
            },
          ] as DynamicFieldConfig[])
        : []),
    ];

    this.rowUploadFormData = {
      docTyp: row?.docSeq ?? null,
      docNm: row?.docNm || '',
      ...(isIssueDocUpload
        ? {
            sftCpyDt: mode === 'reupload' ? (row?.sftCpyDt ?? null) : null,
            hrdCpyDt: mode === 'reupload' ? (row?.hrdCpyDt ?? null) : null,
          }
        : {}),
      ...(this.isSignedCopyUpload(mode, row)
        ? { signingDt: mode === 'reupload' ? (row?.signingDt ?? null) : null }
        : {}),
      ...(needsChargeType ? { chrgTyp: row?.chrgTyp ?? null } : {}),
    };

    this.showRowUploadPopup = true;
  }

  closeRowUploadPopup(): void {
    this.showRowUploadPopup = false;
    this.rowUploadRow = null;
    this.rowUploadFile = null;
    this.rowUploadError = '';
  }

  onRowUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    this.rowUploadFile = input.files?.[0] ?? null;
    this.rowUploadError = '';

    // Lets the same file be picked again after a cancel.
    input.value = '';
  }

  /**
   * Save button: validates the form, then saves. The form isn't submitted
   * through DynamicFormComponent.submit(): when every field is read-only
   * (Client Approval has only the disabled type / name) the FormGroup's
   * status is DISABLED, so `valid` is false and submit() never emits —
   * Save did nothing. `invalid` is false for a disabled group, so this works
   * for every mode.
   */
  triggerRowUploadSave(): void {
    const form = this.rowUploadForm?.form;

    if (form?.invalid) {
      form.markAllAsTouched();

      if (!this.rowUploadFile) {
        this.rowUploadError = 'DOCUMENT.FILE_REQUIRED';
      }

      return;
    }

    this.saveRowUpload(form ? form.value : {});
  }

  /** POST /crdt-prpsl/fo-ltr/save-doc */
  saveRowUpload(formValue: any): void {
    const row = this.rowUploadRow;
    const mode = this.rowUploadMode;
    const crdtFoLtrSeq = Number(
      row?.crdtFoLtrSeq ??
        (mode === 'clientApproval'
          ? this.clientApprovalClient?.crdtFoLtrSeq
          : this.pendingApprovalClient?.crdtFoLtrSeq) ??
        0,
    );
    const rowDocSeq = Number(row?.crdtFoLtrDocSeq ?? 0);

    if (crdtFoLtrSeq <= 0 || rowDocSeq <= 0) {
      this.toast.error('FACILITIES.MSG.DOC_SEQ_MISSING');
      return;
    }

    if (!this.rowUploadFile) {
      this.rowUploadError = 'DOCUMENT.FILE_REQUIRED';
      return;
    }

    if (this.rowUploadSaving) {
      return;
    }

    const today = new Date().toISOString().slice(0, 10);
    const signedCopy = this.isSignedCopyUpload(mode, row);

    // Signed copy (first upload on the 4813 row, or re-upload of the signed
    // row at 4814): crdtFoLtrDocSeq is that row; no aprvngDt / uploadDt /
    // prntDocSeq.
    if (signedCopy) {
      this.postRowUpload(
        buildFoLtrSignedCopyFormData({
          crdtFoLtrSeq,
          docSeq: Number(row?.docSeq),
          crdtFoLtrDocSeq: rowDocSeq,
          sftCpyDt: formValue?.sftCpyDt,
          hrdCpyDt: formValue?.hrdCpyDt,
          signingDt: formValue?.signingDt,
          chrgTyp: formValue?.chrgTyp,
          file: this.rowUploadFile,
        }),
        mode,
      );
      return;
    }

    const formData = new FormData();

    formData.append('crdtFoLtrSeq', String(crdtFoLtrSeq));
    formData.append('docSeq', String(row?.docSeq ?? ''));
    formData.append('docDesc', row?.docDesc || '');
    // save-doc rejects the request without aprvngDt, so it is always sent.
    formData.append('aprvngDt', row?.aprvngDt || (mode === 'clientApproval' ? '' : today));
    formData.append('uploadDt', today);
    formData.append('delFlg', '0');

    // Same row: the accepted copy of doc 36, or the 4814 document re-uploaded.
    formData.append('crdtFoLtrDocSeq', String(rowDocSeq));

    if (mode !== 'clientApproval') {
      if (formValue?.sftCpyDt) {
        formData.append('sftCpyDt', formValue.sftCpyDt);
      }

      if (formValue?.hrdCpyDt) {
        formData.append('hrdCpyDt', formValue.hrdCpyDt);
      }

    }

    formData.append('file', this.rowUploadFile);

    this.postRowUpload(formData, mode);
  }

  /** POST /crdt-prpsl/fo-ltr/save-doc for the row upload popup, then refreshes its list. */
  private postRowUpload(
    formData: FormData,
    mode: 'clientApproval' | 'signedCopy' | 'reupload',
  ): void {
    this.rowUploadSaving = true;

    this.creditService.saveFoLtrDoc(formData).subscribe({
      next: (response: any) => {
        this.rowUploadSaving = false;

        // save-doc can return 200 with an error body; the interceptor already toasted it.
        if (response?.__hasError) {
          return;
        }

        if (response?.error) {
          this.toast.error(response.error);
          return;
        }

        this.toast.success('DOCUMENT.SAVED_SUCCESSFULLY');

        this.closeRowUploadPopup();

        if (mode === 'clientApproval') {
          this.loadClientApprovalList();
          this.loadClientApprovalDocuments();
        } else {
          this.refreshPendingApprovals();
        }
      },

      error: (error: any) => {
        console.error('Unable to upload document:', error);

        this.rowUploadSaving = false;
        this.toast.error(error?.error?.message || error?.error?.error || 'DOCUMENT.SAVE_FAILED');
      },
    });
  }

  pendingDraftActions: TableAction[] = [
    {
      icon: 'images/edit-icon.png',
      tooltip: 'FACILITIES.RECOMMENDATIONS',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-full',
      iconSize: 'sm',
      // Recommendations (the changes a document needs) can be recorded
      // against every pending draft document.
      onClick: (row: any) => this.openRecommendationPopup(row),
    },
    {
      icon: 'images/Tick-2.png',
      tooltip: 'COMMON.APPROVE',
      class: 'w-8 h-8 flex items-center justify-center hover:bg-green-100 rounded-full',
      iconSize: 'sm',
      onClick: (row: any) => this.openApproveDraftPopup(row),
    },
  ];

  // ================= RECOMMENDATIONS =================
  showRecommendationPopup = false;
  recommendationFields: DynamicFieldConfig[] = [];
  recommendationFormData: any = {};
  private recommendationRow: any = null;

  openRecommendationPopup(row: any): void {
    this.recommendationRow = row;

    this.recommendationFormData = { cmmnt: '' };

    this.recommendationFields = [
      {
        key: 'cmmnt',
        label: 'FACILITIES.COMMENT',
        type: 'text',
        validations: {
          required: true,
        },
      },
    ];

    this.showRecommendationPopup = true;
  }

  /** POST /crdt-prpsl/fo-ltr/add-recommendation */
  handleRecommendationSave = (form: any) => {
    const crdtFoLtrDocSeq = Number(this.recommendationRow?.crdtFoLtrDocSeq ?? 0);

    if (crdtFoLtrDocSeq <= 0) {
      this.toast.error('FACILITIES.MSG.DOC_SEQ_MISSING');
      return;
    }

    this.creditService
      .addFoLtrRecommendation({
        crdtFoLtrDocSeq,
        cmmnt: form?.cmmnt || '',
      })
      .subscribe({
        next: (response: any) => {
          // A 200 can still carry an error body: the error interceptor has already toasted it.
          if (response?.__hasError) {
            return;
          }

          if (response?.error) {
            this.toast.error(response.error);
            return;
          }

          this.toast.success(response?.message || 'FACILITIES.MSG.RECOMMENDATION_ADDED');

          this.recommendationRow = null;

          this.refreshPendingDrafts();
        },

        error: (error: any) => {
          console.error('Unable to add recommendation:', error);

          this.toast.error(
            error?.error?.message || error?.error?.error || 'FACILITIES.MSG.UNABLE_ADD_RECOMMENDATION',
          );
        },
      });
  };

  // ================= APPROVE CONFIRMATION =================
  showApproveDraftPopup = false;
  approveDraftMessage = '';
  private draftToApprove: any = null;

  openApproveDraftPopup(row: any): void {
    this.draftToApprove = row;

    this.approveDraftMessage = this.translate.instant('FACILITIES.CONFIRM_APPROVE', {
      name: row?.docNm || this.translate.instant('FACILITIES.THIS_DOCUMENT'),
    });

    this.showApproveDraftPopup = true;
  }

  confirmApproveDraft(): void {
    const row = this.draftToApprove;

    this.draftToApprove = null;

    this.approveDraftDocument(row);
  }

  /** POST /crdt-prpsl/fo-ltr/ro-approve-draft-docs?crdtFoLtrDocSeq= */
  approveDraftDocument(row: any): void {
    const crdtFoLtrDocSeq = Number(row?.crdtFoLtrDocSeq ?? 0);

    if (crdtFoLtrDocSeq <= 0) {
      this.toast.error('FACILITIES.MSG.DOC_SEQ_MISSING');
      return;
    }

    this.creditService.approveDraftFoLtrDocument(crdtFoLtrDocSeq).subscribe({
      next: (response: any) => {
        // A 200 can still carry an error body: the error interceptor has already toasted it.
        if (response?.__hasError) {
          return;
        }

        if (response?.error) {
          this.toast.error(response.error);
          return;
        }

        this.toast.success(response?.message || 'FACILITIES.MSG.DOC_APPROVED_SUCCESS');

        this.refreshPendingDrafts();
      },

      error: (error: any) => {
        console.error('Draft document approval failed:', error);

        this.toast.error(
          error?.error?.message || error?.error?.error || 'FACILITIES.MSG.UNABLE_APPROVE_DOC',
        );
      },
    });
  }

  /** Reject — icon only for now, the flow comes in a later step. */
  /* ================= DOWNLOAD ================= */
  downloadFile(row: any): void {
    if (!row?.docImg) {
      this.toast.info('FACILITIES.MSG.NO_FILE');
      return;
    }

    try {
      // Strip data URL prefix if present
      let base64Data = row.docImg;
      if (base64Data.includes(',')) {
        base64Data = base64Data.split(',')[1];
      }

      // Detect mime type and extension from base64 signature
      let mimeType = 'application/octet-stream';
      let extension = '';

      if (base64Data.startsWith('/9j/')) {
        mimeType = 'image/jpeg';
        extension = '.jpg';
      } else if (base64Data.startsWith('iVBOR')) {
        mimeType = 'image/png';
        extension = '.png';
      } else if (base64Data.startsWith('JVBER')) {
        mimeType = 'application/pdf';
        extension = '.pdf';
      } else if (base64Data.startsWith('UklGR')) {
        mimeType = 'image/webp';
        extension = '.webp';
      } else if (base64Data.startsWith('R0lGOD')) {
        mimeType = 'image/gif';
        extension = '.gif';
      } else if (base64Data.startsWith('UEsDB')) {
        // ZIP-based Office formats — peek inside to detect type
        const decoded = atob(base64Data.substring(0, 200));

        if (decoded.includes('word/')) {
          mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
          extension = '.docx';
        } else if (decoded.includes('xl/')) {
          mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          extension = '.xlsx';
        } else if (decoded.includes('ppt/')) {
          mimeType = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
          extension = '.pptx';
        } else {
          mimeType = 'application/zip';
          extension = '.zip';
        }
      }

      // Convert base64 to blob
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Build filename — avoid double extension
      const baseName = row.docNm || 'document';
      const hasExtension = /\.[a-z0-9]{2,5}$/i.test(baseName);
      const fileName = hasExtension ? baseName : `${baseName}${extension}`;

      // Trigger download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      this.toast.success('FACILITIES.MSG.DOWNLOAD_STARTED');
    } catch (error) {
      console.error('Download failed:', error);
      this.toast.error('FACILITIES.MSG.DOWNLOAD_FAILED');
    }
  }

  /** Same document rows as Pending Drafts, so the same columns. */
  private initClientRecommendationColumns(): void {
    this.clientRecommendationColumns = [
      {
        key: 'crdtFoLtrSeq',
        label: 'FACILITIES.OFFER_LETTER_ID',
        type: 'text',
      },
      {
        key: 'clntNm',
        label: 'FACILITIES.INSTITUTION_NAME',
        type: 'text',
      },
      {
        key: 'docNm',
        label: 'FACILITIES.DOCUMENT_NAME',
        type: 'link',
        onClick: (row: any) => this.downloadFile(row),
      },
      {
        key: 'statusLabel',
        label: 'COMMON.STATUS',
        type: 'text',
      },
      {
        key: 'uploadDt',
        label: 'FACILITIES.UPLOAD_DATE',
        type: 'date',
      },
    ];
  }

  private initCreditProposalColumns(): void {
    this.creditProposalColumns = [
      {
        key: 'crdtFoLtrSeq',
        label: 'FACILITIES.OFFER_LETTER_ID',
        type: 'text',
      },
      {
        key: 'clntNm',
        label: 'FACILITIES.INSTITUTION_NAME',
        type: 'text',
      },
      {
        key: 'statusLabel',
        label: 'COMMON.STATUS',
        type: 'text',
      },
      {
        key: 'crtdBy',
        label: 'COMMON.CREATED_BY',
        type: 'text',
      },
      {
        key: 'crtdDt',
        label: 'COMMON.CREATED_DATE',
        type: 'date',
      },
      {
        key: 'lastUpdDt',
        label: 'FACILITIES.LAST_UPDATED',
        type: 'date',
      },
    ];
  }

  creditProposalActions: TableAction[] = [
    {
      icon: 'images/upload.png',
      tooltip: 'FACILITIES.UPLOAD_OFFER_LETTER_DOCUMENTS',
      class: 'w-6 h-6 flex items-center justify-center rounded-full',
      iconSize: 'sm',
      onClick: (row: CreditProposalForLetterRow) => this.openLetterDocuments(row),
      // Upload and download are Account Manager (LNLY_AM) actions only; the
      // popup handles doc 36 (Stage 1), so it stays off the Approved tab.
      visible: (row: CreditProposalForLetterRow) =>
        this.activeTab !== 'approved' && this.isAccountManager && Number(row?.crdtFoLtrSeq) > 0,
    },
    {
      icon: 'images/upload.png',
      tooltip: 'FACILITIES.UPLOAD_ISSUE_DOCUMENTS',
      class: 'w-6 h-6 flex items-center justify-center rounded-full',
      iconSize: 'sm',
      // Same documents page as the eye icon, but editable.
      onClick: (row: CreditProposalForLetterRow) => this.goToLetterDocuments(row, false),
      // FOL new flow, Stages 2–3: the Account Manager (LNLY_AM) uploads the
      // issue documents and the other documents once the letter is approved.
      visible: (row: CreditProposalForLetterRow) =>
        this.activeTab === 'approved' &&
        this.isAccountManager &&
        Number(row?.crdtFoLtrSeq) > 0,
    },
    {
      icon: this.viewIcon,
      tooltip: 'FACILITIES.VIEW_OFFER_LETTER_DOCUMENTS',
      class: 'action-view',
      onClick: (row: CreditProposalForLetterRow) => this.goToLetterDocuments(row),
      // Shown on both the All and Approved tabs — every letter with a sequence
      // has a documents page to open.
      visible: (row: CreditProposalForLetterRow) => Number(row?.crdtFoLtrSeq) > 0,
    },
    {
      icon: 'images/download.png',
      tooltip: 'FACILITIES.DOWNLOAD_OFFER_LETTER',
      class: 'w-6 h-6 flex items-center justify-center rounded-full',
      iconSize: 'sm',
      onClick: (row: CreditProposalForLetterRow) => this.downloadOfferLetter(row),
      // Upload and download are Account Manager (LNLY_AM) actions only.
      visible: (row: CreditProposalForLetterRow) =>
        this.isAccountManager && Number(row?.crdtFoLtrSeq) > 0,
    },
  ];

  // ================= NAVIGATE TO OFFER LETTER DOCUMENTS PAGE =================
  /** readOnly (eye icon) adds `mode=view`, which makes app-document read-only. */
  goToLetterDocuments(row: CreditProposalForLetterRow, readOnly = true): void {
    if (!row?.crdtFoLtrSeq || row.crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_NA');
      return;
    }

    this.router.navigate(['/facility-offer-letter-documents', row.crdtFoLtrSeq], {
      queryParams: readOnly ? { mode: 'view' } : {},
    });
  }

  // ================= DOWNLOAD OFFER LETTER (.docx) =================
  downloadOfferLetter(row: CreditProposalForLetterRow): void {
    const crdtPrpslSeq = Number(row?.crdtPrpslSeq);

    if (!crdtPrpslSeq || crdtPrpslSeq <= 0) {
      this.toast.error('FACILITIES.MSG.CREDIT_PROPOSAL_SEQ_NA');
      return;
    }

    if (this.downloadingOfferLetterSeq === crdtPrpslSeq) {
      return;
    }

    this.downloadingOfferLetterSeq = crdtPrpslSeq;

    forkJoin({
      foLtrParams: this.creditService.getFacilityOfferLetterParams(crdtPrpslSeq),
      // The letter's VCM snapshot (trm-params-fol) when there is one.
      trmParams: this.letterTermParams$(Number(row?.crdtFoLtrSeq ?? 0), crdtPrpslSeq),
      covenants: this.loadOfferLetterCovenants(crdtPrpslSeq),
      outcomes: this.loadOfferLetterOutcomes(crdtPrpslSeq),
    }).subscribe({
      next: ({ foLtrParams, trmParams, covenants, outcomes }) => {
        this.offerLetterExport
          .exportToWord(foLtrParams, trmParams, covenants, outcomes, `Facility-Offer-Letter-${crdtPrpslSeq}`)
          .catch((error) => {
            console.error('Unable to generate offer letter document:', error);
            this.toast.error('FACILITIES.MSG.UNABLE_GENERATE_OFFER_LETTER');
          })
          .finally(() => {
            this.downloadingOfferLetterSeq = null;
          });
      },
      error: (error: any) => {
        console.error('Unable to load offer letter parameters:', error);

        this.downloadingOfferLetterSeq = null;
        this.toast.error(
          error?.error?.message || error?.error?.error || 'FACILITIES.MSG.UNABLE_LOAD_OFFER_LETTER_PARAMS',
        );
      },
    });
  }

  /**
   * Covenants saved in the credit proposal's Summary Terms and Conditions.
   * Responses only carry `qsntnSeq` / `ansVal`, so the question text comes
   * from the covenants questionnaire and the answer text from each
   * question's answer list (falling back to the raw value for text answers).
   * Any failure yields an empty list so the letter still downloads.
   */
  private loadOfferLetterCovenants(crdtPrpslSeq: number): Observable<OfferLetterCovenant[]> {
    return forkJoin({
      responses: this.creditService.getCovenantResponses(crdtPrpslSeq),
      questions: this.setup.getActQuestionList(COVENANTS_QSTNR_SEQ),
    }).pipe(
      switchMap(({ responses, questions }) => {
        const questionList: any[] = this.toList(questions);
        // get-covenant-responses may come wrapped (`data` / `responses`) and
        // name the question seq `qsntnSeq` or `qstnSeq`.
        const saved = this.toList(responses, 'responses')
          .filter((response: any) => Number(response?.delFlg ?? 0) !== 1)
          .map((response: any) => ({
            ...response,
            qsntnSeq: Number(response?.qsntnSeq ?? response?.qstnSeq),
          }));

        if (!saved.length) return of([]);

        return forkJoin(
          saved.map((response) =>
            this.setup.getAnswerList(Number(response.qsntnSeq)).pipe(catchError(() => of([]))),
          ),
        ).pipe(
          map((answersPerQuestion) =>
            saved
              .map((response, index) => {
                const question = questionList.find(
                  (item) => Number(item?.qstSeq) === Number(response.qsntnSeq),
                );
                const option = this.toList(answersPerQuestion[index]).find(
                  (answer: any) => Number(answer?.answrSeq) === Number(response.ansVal),
                );

                return {
                  question: String(question?.qstStr ?? '').trim(),
                  answer: String(option ? option.answrStr ?? '' : response.ansVal ?? '').trim(),
                };
              })
              .filter((covenant) => covenant.question),
          ),
        );
      }),
      catchError((error) => {
        console.error('Unable to load covenants for offer letter:', error);
        return of([]);
      }),
    );
  }

  /** The array itself, or the one under `data` / `wrapperKey`; otherwise empty. */
  private toList(response: any, wrapperKey?: string): any[] {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.data)) return response.data;
    if (wrapperKey && Array.isArray(response?.[wrapperKey])) return response[wrapperKey];
    return [];
  }

  /**
   * Lines of the "Outcome" row saved in the credit proposal's Summary Terms
   * and Conditions. List blocks keep one entry per item; paragraph blocks
   * are rich text, flattened to plain lines. Any failure yields an empty list
   * so the letter still downloads.
   */
  private loadOfferLetterOutcomes(crdtPrpslSeq: number): Observable<string[]> {
    return forkJoin({
      response: this.creditService.getCrdtBlck(crdtPrpslSeq, [SUMMARY_TERMS_REF_VAL_SEQ]),
      picklist: this.pickListService
        .loadCodesAsync([PKL_GRP.SUMMARY_TERMS_PARAMETERS])
        .pipe(catchError(() => of(null))),
    }).pipe(
      map(({ response }: { response: any }) => {
        // Same response shapes the Summary Terms section accepts.
        const rows: any[] = [
          response?.blockList,
          response?.data?.blockList,
          response?.data?.content,
          response?.content,
          response?.data,
          response,
        ].find((value) => Array.isArray(value)) ?? [];

        // Rows save the picklist `code`; the description may be blank or renamed, so match either.
        const outcomeCodes = new Set(
          this.pickListService
            .getOptionsSync(PKL_GRP.SUMMARY_TERMS_PARAMETERS)
            .filter((option) => String(option.label ?? '').toLowerCase().includes('outcome'))
            .map((option) => Number(option.value)),
        );

        const outcomeRow = rows
          .filter((row) => Number(row?.delFlg ?? 0) !== 1)
          .map((row) => {
            let data = row?.jsonData;
            if (typeof data === 'string') {
              try {
                data = JSON.parse(data);
              } catch {
                data = {};
              }
            }
            return (Array.isArray(data) ? data[0] : data) ?? {};
          })
          .find(
            (data) =>
              outcomeCodes.has(Number(data?.code)) ||
              String(data?.description ?? '').toLowerCase().includes('outcome'),
          );

        const blocks: any[] = Array.isArray(outcomeRow?.recommendationBlocks) ? outcomeRow.recommendationBlocks : [];

        return blocks
          .flatMap((block) =>
            block?.type !== 'paragraph' && Array.isArray(block?.items)
              ? block.items.map((item: any) => richTextToPlainText(String(item ?? '')))
              : richTextToPlainText(block?.content).split('\n'),
          )
          .map((line: string) => line.trim())
          .filter(Boolean);
      }),
      catchError((error) => {
        console.error('Unable to load outcomes for offer letter:', error);
        return of([]);
      }),
    );
  }

  // ================= OFFER LETTER DOCUMENTS =================
  openLetterDocuments(row: CreditProposalForLetterRow): void {
    if (!row?.crdtFoLtrSeq || row.crdtFoLtrSeq <= 0) {
      this.toast.error('FACILITIES.MSG.OFFER_LETTER_SEQ_NA');
      return;
    }

    this.selectedLetter = row;
    this.letterDocuments = [];
    this.letterCanUpload = false;

    this.showLetterDocumentPopup = true;
  }

  // submit-fo-ltr is gone: doc 36 moves on by itself when it is uploaded.
  closeLetterDocumentPopup(): void {
    this.showLetterDocumentPopup = false;
    this.selectedLetter = null;
    this.letterDocuments = [];
    this.letterCanUpload = false;
  }
}
